import { createFileRoute, useNavigate } from '@tanstack/react-router'
import gWhiteLogo from '@/assets/g white.png'
import React, { useState, useMemo, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth, useUser } from '@clerk/clerk-react'
import { useQueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import {
  getUserFromClerkId,
  getParentUser,
  updateOnboardingInfo,
  updateOnboardingStep,
  inviteChildUser,
} from '../hooks/useUser'
import { getPendingWelcomeKits, updateWelcomeKitAddress } from '../hooks/useWelcomeKit'
import { trackEvent } from '@/lib/posthog'
import AddressAutocomplete from '@/components/AddressAutocomplete'
import { validateAddressWithGoogle, type ValidationResult } from '@/lib/addressValidation'
import { isPendingWelcomeKit } from '@/lib/welcomeKit'
import { BrandedSpinner } from '@/components/BrandedSpinner'
import { getBreaches, useTriggerBreaches } from '@/hooks/useBreaches'
import {
  ChevronRight,
  ShieldCheck,
  Search,
  LifeBuoy,
  Users,
  ShieldAlert,
  Zap,
  Lock,
  Trophy,
  Target,
  CheckCircle2,
  AlertTriangle,
  Shield,
  ArrowRight,
  UserPlus,
  BookOpen,
  Package,
  Plus,
  X,
  Trash2,
  ChevronDown,
  HelpCircle,
  MapPin,
  Compass,
  Home,
  MessageSquare,
  Link,
  Smartphone,
  Phone,
  Building,
  Bell,
  Mail,
  Check,
  LayoutGrid,
  Info,
  Pause,
  Play,
  LayoutDashboard,
} from 'lucide-react'

export const Route = createFileRoute('/_auth/onboarding')({
  component: Onboarding2,
})

// ==============================================================================
// TYPES
// ==============================================================================

type OnboardingPath = 'GUARDIAN' | 'SELF_PROTECTOR' | 'INVITED'

type OnboardingStep =
  // All paths
  | 'NAME_CAPTURE'
  | 'SHIELD_CHECK'
  | 'WELCOME_KIT_ADDRESS'
  | 'COMPLETE'
  // Guardian-specific
  | 'GUARDIAN_WELCOME'
  | 'GUARDIAN_ORIENTATION'
  | 'GUARDIAN_YOUR_ROLE'
  | 'GUARDIAN_PLAN_BUILDER'
  | 'GUARDIAN_INVITE'
  | 'GUARDIAN_EXPLORE_OFFER'
  | 'GUARDIAN_FAMILY_SUMMARY'
  | 'GUARDIAN_WHY_YOU_MATTER'
  | 'GUARDIAN_READY'
  | 'GUARDIAN_BREACH_SCAN'
  | 'SPONSOR_YOUR_ROLE'
  | 'SPONSOR_CONCERN_PROBE'
  | 'SPONSOR_BRIDGE'
  | 'SPONSOR_SCAM_TRANSITION'
  | 'SPONSOR_RESOLUTION'
  // Learner paths (Self-Protector and Invited)
  | 'SELF_WELCOME'
  | 'INVITED_WELCOME'
  | 'MINDSET_QUESTION'
  | 'MINDSET_REVEAL'
  | 'TRIGGERS'
  | 'PERSONAL_INTRO'
  | 'PERSONAL_EXAMPLE'
  | 'PERSONAL_REFRAME'
  | 'EXERCISE_INTRO'
  | 'EXERCISE'
  | 'EXERCISE_FEEDBACK'
  | 'FAMILY_CONNECTION' // Invited only
  | 'PILLARS_INTRO'
  | 'PILLARS_CHOICE'
  // Self-protector orientation (new flow)
  | 'SELF_YOUR_DEFENSE'
  | 'SELF_BREACH_ACTIVATION'
  | 'SELF_BREACH_RESULTS'
  | 'SELF_SCAM_TRANSITION'
  | 'SELF_SCAM_SIMULATION'
  | 'SELF_SCAM_EXAMPLE'
  | 'SELF_SCAM_VOICEMAIL'
  | 'SELF_UNSETTLING'
  | 'SELF_WEVE_GOT_YOU'
  | 'SELF_NEXT_CHOICE'
  | 'SELF_WHAT_TO_EXPECT'
  | 'SELF_CHOOSE_START'

// OnboardingState type for reference (state managed via individual hooks)
// path, currentStep, name, selectedAnswer, selectedHighlights, selectedPillar, invitedEmails

// Family member type for plan builder
type FamilyMember = {
  id: string
  email: string
  name: string
  relationship: 'mom' | 'dad' | 'grandma' | 'grandpa' | 'partner' | 'mother-in-law' | 'father-in-law' | 'other' | ''
  techComfort: 'low' | 'medium' | 'high' | ''
  focusAreas: string
  personalMessage: string
  isExpanded: boolean
}

type PlanVisualStyle = 'nodes' | 'cards' | 'orbit'

// Sponsor concern types for personalization
type SponsorConcern =
  | 'family_communication'
  | 'link_checking'
  | 'scam_awareness'
  | 'financial_protection'

// ==============================================================================
// PATH DETECTION
// ==============================================================================

function determineUserPath(user: any): OnboardingPath {
  // Invited users have a parentUserId set
  if (user?.parentUserId) return 'INVITED'

  // Sponsors have subscriptions with quantity > 1 (licenses to share)
  const totalLicenses =
    user?.subscriptions?.reduce(
      (acc: number, sub: any) =>
        sub.status !== 'CANCELED' ? acc + sub.quantity : acc,
      0
    ) || 0
  const usedLicenses =
    (user?.license === 'PAID' ? 1 : 0) + (user?.childUsers?.length || 0)
  const hasAvailableLicenses = totalLicenses > usedLicenses

  return hasAvailableLicenses ? 'GUARDIAN' : 'SELF_PROTECTOR'
}

// ==============================================================================
// STEP SEQUENCES
// ==============================================================================

const GUARDIAN_STEPS: OnboardingStep[] = [
  'GUARDIAN_WELCOME',
  'NAME_CAPTURE',
  'GUARDIAN_PLAN_BUILDER',
  'GUARDIAN_FAMILY_SUMMARY',
  'GUARDIAN_WHY_YOU_MATTER',
  'SPONSOR_BRIDGE',
  'SELF_BREACH_ACTIVATION',
  'SELF_BREACH_RESULTS',
  'SPONSOR_SCAM_TRANSITION',
  'SELF_SCAM_VOICEMAIL',
  'SELF_UNSETTLING',
  'SELF_WEVE_GOT_YOU',
]

const SELF_PROTECTOR_STEPS: OnboardingStep[] = [
  'SELF_WELCOME',
  'NAME_CAPTURE',
  'SELF_BREACH_ACTIVATION',
  'SELF_BREACH_RESULTS',
  'SELF_SCAM_TRANSITION',
  'SELF_SCAM_VOICEMAIL',
  'SELF_UNSETTLING',
  'SELF_WEVE_GOT_YOU',
]

const INVITED_STEPS: OnboardingStep[] = [
  'INVITED_WELCOME',
  'NAME_CAPTURE',
  'SELF_BREACH_ACTIVATION',
  'SELF_BREACH_RESULTS',
  'SELF_SCAM_TRANSITION',
  'SELF_SCAM_VOICEMAIL',
  'SELF_UNSETTLING',
  'SELF_WEVE_GOT_YOU',
]

function getStepsForPath(path: OnboardingPath, hasWelcomeKit: boolean): OnboardingStep[] {
  let steps: OnboardingStep[]
  switch (path) {
    case 'GUARDIAN':
      steps = [...GUARDIAN_STEPS]
      break
    case 'SELF_PROTECTOR':
      steps = [...SELF_PROTECTOR_STEPS]
      break
    case 'INVITED':
      steps = [...INVITED_STEPS]
      break
  }

  // Insert welcome kit address step for non-invited users with pending kits
  if (hasWelcomeKit && path !== 'INVITED') {
    if (path === 'GUARDIAN') {
      // After family plan builder for guardians
      const planBuilderIndex = steps.indexOf('GUARDIAN_PLAN_BUILDER')
      if (planBuilderIndex !== -1) {
        steps.splice(planBuilderIndex + 1, 0, 'WELCOME_KIT_ADDRESS')
      }
    } else {
      // After name capture for self-protectors
      const nameCaptureIndex = steps.indexOf('NAME_CAPTURE')
      if (nameCaptureIndex !== -1) {
        steps.splice(nameCaptureIndex + 1, 0, 'WELCOME_KIT_ADDRESS')
      }
    }
  }

  return steps
}

// ==============================================================================
// MAIN COMPONENT
// ==============================================================================

function Onboarding2() {
  const navigate = useNavigate()
  const { user: clerkUser } = useUser()
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const userQuery = getUserFromClerkId(clerkUser?.id || '', {
    enabled: !!clerkUser,
  })
  const parentUserQuery = getParentUser({ enabled: !!userQuery.data?.parentUserId })
  const updateInfo = updateOnboardingInfo()
  const updateStep = updateOnboardingStep()
  const inviteChildUserMutation = inviteChildUser()
  const pendingKitsQuery = getPendingWelcomeKits()
  const breachesQuery = getBreaches()

  // Check if user has pending welcome kits
  const hasPendingWelcomeKit = isPendingWelcomeKit(pendingKitsQuery.data)

  // Dev mode path override
  const [forcedWelcomeKit, setForcedWelcomeKit] = useState<boolean | null>(null)
  const showWelcomeKitVariant = forcedWelcomeKit ?? hasPendingWelcomeKit

  // Dev mode path override
  const [forcedPath, setForcedPath] = useState<OnboardingPath | null>(null)
  const [forceVoicemailRegenerate, setForceVoicemailRegenerate] = useState(false)

  // Determine the user's path
  const detectedPath = useMemo(
    () => determineUserPath(userQuery.data),
    [userQuery.data]
  )
  const currentPath = forcedPath || detectedPath

  // Get steps for current path
  const steps = useMemo(() => getStepsForPath(currentPath, showWelcomeKitVariant), [currentPath, showWelcomeKitVariant])

  // Onboarding state
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(steps[0])

  // Funnel instrumentation: fire one event per onboarding screen view so
  // PostHog can show exactly where users drop off. Client-side only, so this
  // adds no API calls or Vercel function invocations.
  useEffect(() => {
    if (!currentStep) return
    trackEvent('onboarding_step_viewed', {
      step: currentStep,
      path: currentPath,
      step_number: steps.indexOf(currentStep) + 1,
      total_steps: steps.length,
    })
  }, [currentStep, currentPath, steps])
  const [name, setName] = useState(userQuery.data?.name || '')
  const userEmail = clerkUser?.primaryEmailAddress?.emailAddress
  // Prefer the name the user typed on the first step (a first name or
  // nickname). Fall back to Clerk's first name only, never the full name:
  // a scammer who says "Hi Lee" sounds far more plausible than "Hi Lee Mora".
  const displayName =
    name.trim() ||
    clerkUser?.firstName?.trim() ||
    clerkUser?.fullName?.trim().split(/\s+/)[0] ||
    'there'
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null)
  const [selectedHighlights, setSelectedHighlights] = useState<string[]>([])
  const [selectedPillar, setSelectedPillar] = useState<'protect' | 'detect' | null>(null)
  const [selectedConcerns, setSelectedConcerns] = useState<SponsorConcern[]>([])
  const [weeklyTipsEnabled, setWeeklyTipsEnabled] = useState(true)
  const [invitedEmails, setInvitedEmails] = useState<string[]>([])
  const [prefetchedVoicemail, setPrefetchedVoicemail] = useState<{
    audioBase64: string
    mimeType?: string
  } | null>(null)
  const [isPrefetchingVoicemail, setIsPrefetchingVoicemail] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [completeError, setCompleteError] = useState<string | null>(null)

  // Family plan builder state
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([])
  const [planVisualStyle, setPlanVisualStyle] = useState<PlanVisualStyle>('nodes')

  // Shield check state
  const [shieldCheckProgress, setShieldCheckProgress] = useState(0)
  const [shieldCheckComplete, setShieldCheckComplete] = useState(false)

  // Welcome kit address state
  const [addressInput, setAddressInput] = useState('')
  const [selectedAddress, setSelectedAddress] = useState<{
    address: string
    city: string
    state: string
    country: string
    postcode: string
    unitNumber?: string
  } | null>(null)
  const updateWelcomeKitMutation = updateWelcomeKitAddress()

  // Update name from user data when it loads
  useEffect(() => {
    if (userQuery.data?.name) {
      setName(userQuery.data.name)
    }
  }, [userQuery.data?.name])

  // Track step index and path for correct reset/advance behaviour
  const stepIndexRef = useRef(0)
  const prevPathRef = useRef<OnboardingPath | null>(null)

  // Keep step index ref in sync when current step is valid
  useEffect(() => {
    const idx = steps.indexOf(currentStep)
    if (idx >= 0) {
      stepIndexRef.current = idx
    }
  }, [currentStep, steps])

  // Advance step when steps change — preserve position instead of resetting
  useEffect(() => {
    const previousPath = prevPathRef.current
    const pathChanged = previousPath != null && previousPath !== currentPath
    prevPathRef.current = currentPath

    if (pathChanged) {
      // Path genuinely changed. Check if the current step exists in the new path.
      const newStepIndex = steps.indexOf(currentStep)
      if (newStepIndex >= 0) {
        const oldSteps = getStepsForPath(previousPath!, showWelcomeKitVariant)
        const missedSteps = steps
          .slice(0, newStepIndex)
          .filter(s => !oldSteps.includes(s))

        if (missedSteps.length > 0) {
          setCurrentStep(missedSteps[0])
          return
        }
        return
      }
      const nextIndex = Math.min(stepIndexRef.current, steps.length - 1)
      setCurrentStep(steps[Math.max(0, nextIndex)])
      return
    }

    if (!steps.includes(currentStep)) {
      const nextIndex = Math.min(stepIndexRef.current, steps.length - 1)
      setCurrentStep(steps[Math.max(0, nextIndex)])
    }
  }, [currentPath, steps, currentStep, showWelcomeKitVariant])

  // Preload welcome kit image so it's cached before the user reaches that step
  useEffect(() => {
    const img = new Image()
    img.src = '/welcomekit.webp'
  }, [])

  useEffect(() => {
    if (currentStep !== 'SELF_BREACH_ACTIVATION' && currentStep !== 'SPONSOR_BRIDGE') return
    if (isPrefetchingVoicemail || prefetchedVoicemail) return
    if (!displayName.trim()) return

    const preloadVoicemail = async () => {
      setIsPrefetchingVoicemail(true)
      try {
        const token = await getToken()
        const response = await fetch(`${import.meta.env.VITE_API_URL}/app/voicemail/onboarding`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ name: displayName }),
        })
        if (!response.ok) return
        const data = await response.json()
        if (data?.audioBase64) {
          setPrefetchedVoicemail({
            audioBase64: data.audioBase64,
            mimeType: data.mimeType,
          })
        }
      } catch (error) {
        console.warn('[voicemail-prefetch] Failed to preload voicemail audio:', error)
      } finally {
        setIsPrefetchingVoicemail(false)
      }
    }

    preloadVoicemail()
  }, [currentStep, displayName, getToken, isPrefetchingVoicemail, prefetchedVoicemail])

  // Navigation
  const currentIndex = steps.indexOf(currentStep)
  const canGoBack = currentIndex > 0
  const canGoNext = currentIndex < steps.length - 1

  const goToNextStep = async () => {
    const leavingStep = currentStep

    if (canGoNext) {
      setCurrentStep(steps[currentIndex + 1])
    }

    // Fire mutations for the step we just left (non-blocking)
    if (leavingStep === 'NAME_CAPTURE' && name.trim()) {
      updateInfo.mutate({ name: name.trim() })
    }

    // Send invitations for family members when leaving the plan builder
    if (leavingStep === 'GUARDIAN_PLAN_BUILDER' && familyMembers.length > 0) {
      // Only invite members who haven't been invited yet
      const membersToInvite = familyMembers.filter(
        m => m.email.trim() && !invitedEmails.includes(m.email.trim().toLowerCase())
      )
      const newlyInvited: string[] = []

      for (const member of membersToInvite) {
        try {
          await inviteChildUserMutation.mutateAsync({
            email: member.email.trim(),
            name: member.name || undefined,
            relationship: member.relationship || undefined,
            techComfort: member.techComfort || undefined,
            focusAreas: member.focusAreas || undefined,
            personalMessage: member.personalMessage || undefined,
          })
          newlyInvited.push(member.email.trim().toLowerCase())
        } catch (error) {
          console.error(`Failed to invite ${member.email}:`, error)
          // Continue with other invitations even if one fails
        }
      }

      // Update the invitedEmails state to prevent duplicate invitations
      if (newlyInvited.length > 0) {
        setInvitedEmails(prev => [...prev, ...newlyInvited])
      }
    }
  }

  const goToPrevStep = () => {
    if (canGoBack) {
      setCurrentStep(steps[currentIndex - 1])
    }
  }

  // Finish onboarding and leave. Every path out (Skip intro, the final step's
  // button, the Complete step) goes through here. It must never leave the
  // button stuck on "Loading..." (TD-037): the requests are bounded by
  // timeouts in useUser.ts, failures surface an error and re-enable the
  // button, and we seed the onboardingStep cache from the mutation result
  // instead of waiting on a further refetch round trip before navigating.
  const finishOnboarding = async (
    destination: string = '/',
    searchOverride?: Record<string, unknown>
  ) => {
    if (isSubmitting) return
    setIsSubmitting(true)
    setCompleteError(null)
    try {
      // The name is already saved when leaving NAME_CAPTURE; only resend it
      // if it changed since, so the final click is a single request.
      const trimmedName = name.trim()
      if (trimmedName && trimmedName !== (userQuery.data?.name ?? '').trim()) {
        await updateInfo.mutateAsync({ name: trimmedName })
      }
      await updateStep.mutateAsync('COMPLETE')
      queryClient.setQueryData(['user', 'onboardingStep'], 'COMPLETE')
      navigate({
        to: destination,
        search: searchOverride ?? { invitationcode: undefined, __clerk_ticket: undefined },
      })
    } catch (error) {
      console.error('Failed to complete onboarding:', error)
      Sentry.captureException(error, { tags: { area: 'onboarding-complete' } })
      setCompleteError(
        "We couldn't finish setting up your account. Please check your connection and try again."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const skipToEnd = () => finishOnboarding('/')
  const completeOnboarding = () => finishOnboarding('/')
  const completeOnboardingTo = (
    destination: string,
    searchOverride?: Record<string, unknown>
  ) => finishOnboarding(destination, searchOverride)

  // Exercise helpers
  const toggleHighlight = (id: string) => {
    setSelectedHighlights((prev) =>
      prev.includes(id) ? prev.filter((h) => h !== id) : [...prev, id]
    )
  }

  // Invitation helpers
  const handleSendInvitation = async () => {
    if (!inviteEmail.trim()) return

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(inviteEmail.trim())) {
      setInviteError('Please enter a valid email address.')
      return
    }

    if (invitedEmails.includes(inviteEmail.trim().toLowerCase())) {
      setInviteError('You have already sent an invitation to this email.')
      return
    }

    setInviteError(null)
    try {
      await inviteChildUserMutation.mutateAsync({ email: inviteEmail.trim() })
      setInvitedEmails((prev) => [...prev, inviteEmail.trim().toLowerCase()])
      setInviteEmail('')
      queryClient.invalidateQueries({ queryKey: ['users'] })
    } catch (error) {
      setInviteError('Failed to send invitation. Please try again.')
    }
  }

  // Loading state
  if (userQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <BrandedSpinner size="xl" />
      </div>
    )
  }

  // Get sponsor name for invited users: "Sarah got you access" beats
  // "Your family member got you access". TD-017.
  const sponsorName = userQuery.data?.parentUserId
    ? parentUserQuery.data?.name?.trim() || 'Your family member'
    : ''

  // Animation variants
  const slideVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
  }

  return (
    <div className="onboarding-container min-h-screen bg-white flex flex-col">
      {/* Dev Mode Toggle */}
      {import.meta.env.DEV && (
        <div className="fixed top-2 left-2 z-50 bg-black/80 text-white text-xs rounded-lg p-2 flex flex-col gap-2">
          <div className="flex gap-2">
            <button
              onClick={() => setForcedPath('GUARDIAN')}
              className={`cursor-pointer hover:text-violet-300 ${
                forcedPath === 'GUARDIAN' ? 'text-violet-400' : 'text-white/60'
              }`}
            >
              Guardian
            </button>
            <button
              onClick={() => setForcedPath('SELF_PROTECTOR')}
              className={`cursor-pointer hover:text-violet-300 ${
                forcedPath === 'SELF_PROTECTOR' ? 'text-violet-400' : 'text-white/60'
              }`}
            >
              Self
            </button>
            <button
              onClick={() => setForcedPath('INVITED')}
              className={`cursor-pointer hover:text-violet-300 ${
                forcedPath === 'INVITED' ? 'text-violet-400' : 'text-white/60'
              }`}
            >
              Invited
            </button>
            <button
              onClick={() => setForcedPath(null)}
              className={`cursor-pointer hover:text-emerald-300 ${
                !forcedPath ? 'text-emerald-400' : 'text-white/60'
              }`}
            >
              Auto
            </button>
          </div>
          <div className="flex gap-2 border-t border-white/20 pt-2">
            <span className="text-white/40">Kit:</span>
            <button
              onClick={() => setForcedWelcomeKit(true)}
              className={`cursor-pointer hover:text-amber-300 ${
                forcedWelcomeKit === true ? 'text-amber-400' : 'text-white/60'
              }`}
            >
              +Kit
            </button>
            <button
              onClick={() => setForcedWelcomeKit(false)}
              className={`cursor-pointer hover:text-amber-300 ${
                forcedWelcomeKit === false ? 'text-amber-400' : 'text-white/60'
              }`}
            >
              No Kit
            </button>
            <button
              onClick={() => setForcedWelcomeKit(null)}
              className={`cursor-pointer hover:text-emerald-300 ${
                forcedWelcomeKit === null ? 'text-emerald-400' : 'text-white/60'
              }`}
            >
              Auto
            </button>
          </div>
          <label className="flex items-center gap-2 border-t border-white/20 pt-2 text-white/70">
            <input
              type="checkbox"
              checked={forceVoicemailRegenerate}
              onChange={(event) => setForceVoicemailRegenerate(event.target.checked)}
              className="accent-violet-400"
            />
            Regen voicemail
          </label>
        </div>
      )}

      {/* Top Header */}
      <div className="fixed top-0 left-0 right-0 p-4 sm:p-6 md:p-10 flex justify-between items-center max-w-[1000px] mx-auto w-full z-40">
        <span className="text-sm text-gray-500">
          Step {currentIndex + 1} of {steps.length}
        </span>
        <button
          onClick={skipToEnd}
          disabled={isSubmitting}
          className="text-gray-500 text-sm font-medium hover:text-violet-600 transition-colors cursor-pointer disabled:opacity-50"
        >
          Skip intro
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-6 sm:p-6 w-full overflow-x-hidden overflow-y-auto pt-24">
        <AnimatePresence mode="wait">
            <motion.div
            key={currentStep}
            variants={slideVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="onboarding-slide max-w-5xl w-full text-center py-10"
          >
            {/* ================================================================
                GUARDIAN PATH STEPS
                ================================================================ */}

            {currentStep === 'GUARDIAN_WELCOME' && (
              <GuardianWelcomeStep onNext={goToNextStep} hasWelcomeKit={showWelcomeKitVariant} />
            )}

            {currentStep === 'GUARDIAN_ORIENTATION' && (
              <GuardianOrientationStep onNext={goToNextStep} />
            )}

            {currentStep === 'GUARDIAN_YOUR_ROLE' && (
              <GuardianYourRoleStep onNext={goToNextStep} />
            )}

            {currentStep === 'GUARDIAN_PLAN_BUILDER' && (
              <GuardianPlanBuilderStep
                guardianName={name}
                guardianAvatarUrl={clerkUser?.imageUrl}
                familyMembers={familyMembers}
                setFamilyMembers={setFamilyMembers}
                visualStyle={planVisualStyle}
                setVisualStyle={setPlanVisualStyle}
                availableLicenses={
                  (userQuery.data?.subscriptions?.reduce(
                    (acc: number, sub: any) =>
                      sub.status !== 'CANCELED' ? acc + sub.quantity : acc,
                    0
                  ) || 0) -
                  ((userQuery.data?.license === 'PAID' ? 1 : 0) +
                    (userQuery.data?.childUsers?.length || 0))
                }
                onNext={goToNextStep}
              />
            )}

            {currentStep === 'GUARDIAN_INVITE' && (
              <GuardianInviteStep
                inviteEmail={inviteEmail}
                setInviteEmail={setInviteEmail}
                invitedEmails={invitedEmails}
                inviteError={inviteError}
                isLoading={inviteChildUserMutation.isPending}
                onSendInvitation={handleSendInvitation}
                onNext={goToNextStep}
                availableLicenses={
                  (userQuery.data?.subscriptions?.reduce(
                    (acc: number, sub: any) =>
                      sub.status !== 'CANCELED' ? acc + sub.quantity : acc,
                    0
                  ) || 0) -
                  ((userQuery.data?.license === 'PAID' ? 1 : 0) +
                    (userQuery.data?.childUsers?.length || 0))
                }
              />
            )}

            {currentStep === 'GUARDIAN_EXPLORE_OFFER' && (
              <GuardianExploreOfferStep
                onExplore={goToNextStep}
                onSkip={() => setCurrentStep('COMPLETE')}
              />
            )}

            {currentStep === 'GUARDIAN_FAMILY_SUMMARY' && (
              <GuardianFamilySummaryStep
                familyMembers={familyMembers}
                hasWelcomeKit={showWelcomeKitVariant}
                selectedAddress={selectedAddress}
                onNext={goToNextStep}
              />
            )}

            {currentStep === 'GUARDIAN_WHY_YOU_MATTER' && (
              <GuardianWhyYouMatterStep
                onNext={goToNextStep}
                userAvatarUrl={clerkUser?.imageUrl}
                userName={name}
              />
            )}

            {currentStep === 'SPONSOR_BRIDGE' && (
              <SponsorBridgeStep
                onNext={goToNextStep}
                familyMemberNames={familyMembers.filter(m => m.name).map(m => m.name)}
                userAvatarUrl={clerkUser?.imageUrl}
                userName={name}
              />
            )}

            {currentStep === 'SPONSOR_SCAM_TRANSITION' && (
              <SponsorScamTransitionStep onNext={goToNextStep} breachCount={breachesQuery.data?.length || 0} />
            )}

            {currentStep === 'SPONSOR_RESOLUTION' && (
              <SponsorResolutionStep
                onStartTour={() => {
                  completeOnboardingTo('/', {
                    tour: 'active',
                    invitationcode: undefined,
                    __clerk_ticket: undefined,
                  })
                }}
                isSubmitting={isSubmitting}
                breachCount={breachesQuery.data?.length || 0}
              />
            )}

            {currentStep === 'GUARDIAN_READY' && (
              <GuardianReadyStep
                onGoToDashboard={() => navigate({ to: '/', search: { invitationcode: undefined, __clerk_ticket: undefined } })}
              />
            )}

            {currentStep === 'GUARDIAN_BREACH_SCAN' && (
              <GuardianBreachScanStep
                onNext={goToNextStep}
              />
            )}

            {currentStep === 'SPONSOR_YOUR_ROLE' && (
              <SponsorYourRoleStep
                userMarket={userQuery.data?.market || 'GB'}
                onNext={goToNextStep}
              />
            )}

            {currentStep === 'SPONSOR_CONCERN_PROBE' && (
              <SponsorConcernProbeStep
                selectedConcerns={selectedConcerns}
                setSelectedConcerns={setSelectedConcerns}
                onNext={goToNextStep}
                onSkip={() => {
                  // "Show me everything" = select all
                  setSelectedConcerns(['family_communication', 'link_checking', 'scam_awareness', 'financial_protection'])
                  goToNextStep()
                }}
              />
            )}

            {/* ================================================================
                INVITED PATH SPECIFIC STEPS
                ================================================================ */}

            {currentStep === 'INVITED_WELCOME' && (
              <InvitedWelcomeStep
                sponsorName={sponsorName}
                onNext={goToNextStep}
              />
            )}

            {currentStep === 'FAMILY_CONNECTION' && (
              <FamilyConnectionStep
                sponsorName={sponsorName}
                onNext={goToNextStep}
              />
            )}

            {/* ================================================================
                SHARED LEARNER STEPS (SELF-PROTECTOR & INVITED)
                ================================================================ */}

            {currentStep === 'SELF_WELCOME' && (
              <SelfProtectorWelcomeStep onNext={goToNextStep} hasWelcomeKit={showWelcomeKitVariant} />
            )}

            {currentStep === 'NAME_CAPTURE' && (
              <NameCaptureStep
                name={name}
                setName={setName}
                onNext={goToNextStep}
              />
            )}

            {currentStep === 'SELF_YOUR_DEFENSE' && (
              <SelfYourDefenseStep onNext={goToNextStep} />
            )}
            {currentStep === 'SELF_BREACH_ACTIVATION' && (
              <SelfBreachActivationStep onNext={goToNextStep} emailAddress={userEmail} isGuardian={currentPath === 'GUARDIAN'} />
            )}
            {currentStep === 'SELF_BREACH_RESULTS' && (
              <SelfBreachResultsStep onNext={goToNextStep} />
            )}
            {currentStep === 'SELF_SCAM_TRANSITION' && (
              <SelfScamTransitionStep onNext={goToNextStep} breachCount={breachesQuery.data?.length || 0} />
            )}
            {currentStep === 'SELF_SCAM_SIMULATION' && (
              <SelfScamSimulationStep onNext={goToNextStep} />
            )}
            {currentStep === 'SELF_SCAM_EXAMPLE' && (
              <SelfScamExampleStep
                onNext={goToNextStep}
                displayName={displayName}
              />
            )}
            {currentStep === 'SELF_SCAM_VOICEMAIL' && (
              <SelfScamVoicemailStep
                onNext={goToNextStep}
                displayName={displayName}
                forceRegenerate={forceVoicemailRegenerate}
                market={userQuery.data?.market ?? null}
                preloadedAudio={prefetchedVoicemail}
              />
            )}
            {currentStep === 'SELF_UNSETTLING' && (
              <SelfUnsettlingStep onNext={goToNextStep} breachCount={breachesQuery.data?.length || 0} />
            )}
            {currentStep === 'SELF_WEVE_GOT_YOU' && (
              <SelfWeveGotYourBackStep
                onStartLesson={() => {
                  completeOnboardingTo('/', {
                    tour: 'active',
                    invitationcode: undefined,
                    __clerk_ticket: undefined,
                  })
                }}
                isSubmitting={isSubmitting}
              />
            )}
            {currentStep === 'SELF_NEXT_CHOICE' && (
              <SelfNextChoiceStep
                onLearnMore={() => setCurrentStep('SELF_WHAT_TO_EXPECT')}
                onBreachScan={() => completeOnboardingTo('/data-breaches')}
                onSkip={completeOnboarding}
                isSubmitting={isSubmitting}
              />
            )}
            {currentStep === 'SELF_WHAT_TO_EXPECT' && (
              <SelfWhatToExpectStep onNext={goToNextStep} />
            )}
            {currentStep === 'SELF_CHOOSE_START' && (
              <SelfChooseStartStep
                onComplete={completeOnboardingTo}
                isSubmitting={isSubmitting}
              />
            )}

            {currentStep === 'MINDSET_QUESTION' && (
              <MindsetQuestionStep
                selectedAnswer={selectedAnswer}
                setSelectedAnswer={setSelectedAnswer}
                onNext={goToNextStep}
              />
            )}

            {currentStep === 'MINDSET_REVEAL' && (
              <MindsetRevealStep
                isInvited={currentPath === 'INVITED'}
                sponsorName={sponsorName}
                onNext={goToNextStep}
              />
            )}

            {currentStep === 'TRIGGERS' && (
              <TriggersStep onNext={goToNextStep} />
            )}

            {currentStep === 'PERSONAL_INTRO' && (
              <PersonalIntroStep onNext={goToNextStep} />
            )}

            {currentStep === 'PERSONAL_EXAMPLE' && (
              <PersonalExampleStep name={name} onNext={goToNextStep} />
            )}

            {currentStep === 'PERSONAL_REFRAME' && (
              <PersonalReframeStep onNext={goToNextStep} />
            )}

            {currentStep === 'EXERCISE_INTRO' && (
              <ExerciseIntroStep
                isInvited={currentPath === 'INVITED'}
                onNext={goToNextStep}
              />
            )}

            {currentStep === 'EXERCISE' && (
              <ExerciseStep
                name={name}
                selectedHighlights={selectedHighlights}
                toggleHighlight={toggleHighlight}
                onNext={goToNextStep}
              />
            )}

            {currentStep === 'EXERCISE_FEEDBACK' && (
              <ExerciseFeedbackStep
                selectedHighlights={selectedHighlights}
                onNext={goToNextStep}
              />
            )}

            {currentStep === 'PILLARS_INTRO' && (
              <PillarsIntroStep onNext={goToNextStep} />
            )}

            {currentStep === 'PILLARS_CHOICE' && (
              <PillarsChoiceStep
                selectedPillar={selectedPillar}
                setSelectedPillar={setSelectedPillar}
                onNext={goToNextStep}
              />
            )}

            {/* ================================================================
                SHARED STEPS (ALL PATHS)
                ================================================================ */}

            {currentStep === 'SHIELD_CHECK' && (
              <ShieldCheckStep
                isInvited={currentPath === 'INVITED'}
                progress={shieldCheckProgress}
                setProgress={setShieldCheckProgress}
                isComplete={shieldCheckComplete}
                setIsComplete={setShieldCheckComplete}
                onNext={goToNextStep}
              />
            )}

            {currentStep === 'WELCOME_KIT_ADDRESS' && (
              <WelcomeKitAddressStep
                addressInput={addressInput}
                setAddressInput={setAddressInput}
                selectedAddress={selectedAddress}
                setSelectedAddress={setSelectedAddress}
                isSubmitting={updateWelcomeKitMutation.isPending}
                isGuardian={currentPath === 'GUARDIAN'}
                onSubmit={async () => {
                  if (selectedAddress) {
                    // Prepend unit number to address if provided
                    const fullAddress = selectedAddress.unitNumber
                      ? `${selectedAddress.unitNumber}, ${selectedAddress.address}`
                      : selectedAddress.address
                    await updateWelcomeKitMutation.mutateAsync([{
                      address: fullAddress,
                      city: selectedAddress.city,
                      state: selectedAddress.state,
                      country: selectedAddress.country,
                      postcode: selectedAddress.postcode,
                      delivered: false,
                    }])
                  }
                  goToNextStep()
                }}
                onSkip={goToNextStep}
              />
            )}

            {currentStep === 'COMPLETE' && (
              <CompleteStep
                path={currentPath}
                name={name}
                invitedCount={invitedEmails.length}
                isSubmitting={isSubmitting}
                onComplete={completeOnboarding}
                hasWelcomeKit={showWelcomeKitVariant}
                familyMembers={familyMembers}
                weeklyTipsEnabled={weeklyTipsEnabled}
                setWeeklyTipsEnabled={setWeeklyTipsEnabled}
              />
            )}
          </motion.div>
        </AnimatePresence>

        {completeError && (
          <div
            role="alert"
            className="mt-6 max-w-md w-full rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-base text-red-700 text-center"
          >
            {completeError}
          </div>
        )}

        {/* Back Button */}
        {canGoBack && currentStep !== 'COMPLETE' && (
          <button
            onClick={goToPrevStep}
            className="text-gray-400 text-sm font-medium hover:text-violet-600 transition-colors cursor-pointer mt-4"
          >
            ← Back
          </button>
        )}

        {/* Step Tracker */}
        {currentStep !== 'COMPLETE' && (
          <div className="fixed bottom-8 left-1/2 -translate-x-1/2">
            <div className="flex items-center gap-2">
              {steps.filter(s => s !== 'COMPLETE').map((step) => {
                const isCurrent = step === currentStep
                const isPast = steps.indexOf(step) < currentIndex
                return (
                  <div
                    key={step}
                    className="relative flex flex-col items-center p-1 -m-1"
                  >
                    {isCurrent && (
                      <div className="absolute -top-6 text-violet-600">
                        <StepIcon step={step} />
                      </div>
                    )}
                    {/* Indicative only: clickable dots could teleport past
                        required inputs and the side effects (like sending
                        invites) that fire on leaving a step. TD-017. */}
                    <div
                      aria-hidden
                      className={`rounded-full transition-all duration-300 ${
                        isCurrent
                          ? 'w-8 h-2.5 bg-violet-600'
                          : isPast
                            ? 'w-2.5 h-2.5 bg-violet-400'
                            : 'w-2.5 h-2.5 bg-gray-300'
                      }`}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ==============================================================================
// SHARED COMPONENTS
// ==============================================================================

function WelcomeLogo() {
  return (
    <motion.div
      className="w-24 h-24 mb-8 mx-auto"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <img src="/g_logo_violet.png" alt="GranGuard" className="w-full h-full object-contain" />
    </motion.div>
  )
}

function StepIcon({ step }: { step: OnboardingStep }) {
  const iconClass = "w-4 h-4"

  switch (step) {
    case 'GUARDIAN_WELCOME':
    case 'SELF_WELCOME':
    case 'INVITED_WELCOME':
      return <Compass className={iconClass} />
    case 'NAME_CAPTURE':
      return <UserPlus className={iconClass} />
    case 'GUARDIAN_PLAN_BUILDER':
      return <Users className={iconClass} />
    case 'GUARDIAN_ORIENTATION':
      return <BookOpen className={iconClass} />
    case 'GUARDIAN_YOUR_ROLE':
      return <ShieldCheck className={iconClass} />
    case 'GUARDIAN_INVITE':
    case 'GUARDIAN_EXPLORE_OFFER':
      return <Search className={iconClass} />
    case 'GUARDIAN_FAMILY_SUMMARY':
      return <CheckCircle2 className={iconClass} />
    case 'GUARDIAN_WHY_YOU_MATTER':
      return <Users className={iconClass} />
    case 'GUARDIAN_READY':
      return <CheckCircle2 className={iconClass} />
    case 'GUARDIAN_BREACH_SCAN':
      return <Search className={iconClass} />
    case 'SPONSOR_YOUR_ROLE':
      return <Home className={iconClass} />
    case 'SPONSOR_CONCERN_PROBE':
      return <Target className={iconClass} />
    case 'SPONSOR_BRIDGE':
      return <Users className={iconClass} />
    case 'SPONSOR_SCAM_TRANSITION':
      return <ShieldAlert className={iconClass} />
    case 'SPONSOR_RESOLUTION':
      return <Shield className={iconClass} />
    case 'WELCOME_KIT_ADDRESS':
      return <Package className={iconClass} />
    case 'MINDSET_QUESTION':
    case 'MINDSET_REVEAL':
      return <Zap className={iconClass} />
    case 'TRIGGERS':
      return <AlertTriangle className={iconClass} />
    case 'PERSONAL_INTRO':
    case 'PERSONAL_EXAMPLE':
    case 'PERSONAL_REFRAME':
      return <Target className={iconClass} />
    case 'EXERCISE_INTRO':
    case 'EXERCISE':
    case 'EXERCISE_FEEDBACK':
      return <Trophy className={iconClass} />
    case 'FAMILY_CONNECTION':
      return <Users className={iconClass} />
    case 'PILLARS_INTRO':
    case 'PILLARS_CHOICE':
      return <Shield className={iconClass} />
    case 'SHIELD_CHECK':
      return <ShieldCheck className={iconClass} />
    case 'SELF_YOUR_DEFENSE':
      return <Shield className={iconClass} />
    case 'SELF_BREACH_ACTIVATION':
      return <ShieldCheck className={iconClass} />
    case 'SELF_BREACH_RESULTS':
      return <AlertTriangle className={iconClass} />
    case 'SELF_SCAM_TRANSITION':
      return <ShieldAlert className={iconClass} />
    case 'SELF_SCAM_SIMULATION':
      return <AlertTriangle className={iconClass} />
    case 'SELF_SCAM_EXAMPLE':
      return <MessageSquare className={iconClass} />
    case 'SELF_SCAM_VOICEMAIL':
      return <Phone className={iconClass} />
    case 'SELF_NEXT_CHOICE':
      return <Compass className={iconClass} />
    case 'SELF_WHAT_TO_EXPECT':
      return <CheckCircle2 className={iconClass} />
    case 'SELF_CHOOSE_START':
      return <Compass className={iconClass} />
    default:
      return <ChevronRight className={iconClass} />
  }
}

// ==============================================================================
// GUARDIAN PATH STEP COMPONENTS
// ==============================================================================

function GuardianWelcomeStep({ onNext, hasWelcomeKit }: { onNext: () => void; hasWelcomeKit: boolean }) {
  return (
    <>
      <WelcomeLogo />
      <motion.h1
        className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.12, duration: 0.4, ease: 'easeOut' }}
      >
        Welcome to GranGuard
      </motion.h1>
      <motion.p
        className="text-xl md:text-2xl text-gray-600 mb-4 max-w-2xl mx-auto leading-relaxed"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.24, duration: 0.4, ease: 'easeOut' }}
      >
        {hasWelcomeKit
          ? "Let's get your account set up, send your Welcome Kit, and build out your family plan. Don't worry, this should only take a minute."
          : "Let's get your account set up and build out your family plan. Don't worry, this should only take a minute."
        }
      </motion.p>
      <motion.p
        className="text-sm text-gray-500 mb-10"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.36, duration: 0.4, ease: 'easeOut' }}
      >
        You're doing a good thing here.
      </motion.p>
      <motion.button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-lg hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.48, duration: 0.4, ease: 'easeOut' }}
      >
        Let's Begin <ChevronRight className="w-5 h-5" />
      </motion.button>
    </>
  )
}

function GuardianOrientationStep({ onNext }: { onNext: () => void }) {
  const pillars = [
    { icon: <ShieldCheck className="w-6 h-6" />, name: 'PROTECT', desc: 'Secure accounts & devices' },
    { icon: <Search className="w-6 h-6" />, name: 'DETECT', desc: 'Spot scams before they work' },
    { icon: <LifeBuoy className="w-6 h-6" />, name: 'RESPOND', desc: 'Know what to do when targeted' },
    { icon: <Users className="w-6 h-6" />, name: 'CONNECT', desc: 'Protect your family together' },
  ]

  return (
    <>
      <p className="text-xs font-bold text-violet-600 uppercase tracking-widest mb-4">
        What Your Family Will Learn
      </p>
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
        Four pillars of<br />scam defense
      </h1>
      <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
        GranGuard training covers everything they need to stay safe online.
      </p>
      <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto mb-10">
        {pillars.map((pillar) => (
          <div
            key={pillar.name}
            className="p-5 bg-gray-50 rounded-2xl text-center border border-gray-100"
          >
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-violet-600 mx-auto mb-3 shadow-sm">
              {pillar.icon}
            </div>
            <div className="font-bold text-sm text-gray-900 mb-1">
              {pillar.name}
            </div>
            <div className="text-xs text-gray-500">{pillar.desc}</div>
          </div>
        ))}
      </div>
      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-[#1d1d1f] text-white font-bold px-12 py-5 rounded-full hover:bg-black transition-all cursor-pointer shadow-xl hover:scale-[1.02] active:scale-[0.98]"
      >
        Continue <ChevronRight className="w-5 h-5 ml-2" />
      </button>
    </>
  )
}

function GuardianYourRoleStep({ onNext }: { onNext: () => void }) {
  return (
    <>
      <div className="w-20 h-20 bg-gradient-to-br from-violet-100 to-indigo-100 rounded-[24px] flex items-center justify-center mb-8 mx-auto border border-violet-200/50">
        <Shield className="w-10 h-10 text-violet-500" />
      </div>
      <p className="text-xs font-bold text-violet-600 uppercase tracking-widest mb-4">
        Your Role
      </p>
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
        Safety leadership,<br />not surveillance
      </h1>
      <div className="max-w-md mx-auto mb-8 text-left space-y-4">
        <div className="p-5 bg-emerald-50 rounded-2xl border border-emerald-100">
          <p className="font-bold text-emerald-800 mb-2">You CAN see:</p>
          <ul className="text-sm text-emerald-700 space-y-2">
            <li className="flex items-start gap-2">
              <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span>If their protection shield is active and strong</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span>When they've completed defense training</span>
            </li>
          </ul>
        </div>
        <div className="p-5 bg-gray-50 rounded-2xl border border-gray-200">
          <p className="font-bold text-gray-800 mb-2">You CANNOT see:</p>
          <ul className="text-sm text-gray-600 space-y-2">
            <li className="flex items-start gap-2">
              <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400" />
              <span>Their practice results or quiz answers</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400" />
              <span>Their specific data breach details</span>
            </li>
          </ul>
        </div>
      </div>
      <p className="text-gray-500 mb-8 text-sm leading-relaxed max-w-sm mx-auto">
        Your circle is built on trust. You're here to provide the safety net, while they build the skills.
      </p>
      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-[#1d1d1f] text-white font-bold px-12 py-5 rounded-full hover:bg-black transition-all cursor-pointer shadow-xl hover:scale-[1.02] active:scale-[0.98]"
      >
        I Understand <ChevronRight className="w-5 h-5 ml-2" />
      </button>
    </>
  )
}

// ==============================================================================
// GUARDIAN PLAN BUILDER - VISUAL OPTIONS PROTOTYPE
// ==============================================================================

const RELATIONSHIP_OPTIONS = [
  { value: 'mom', label: 'Mom' },
  { value: 'dad', label: 'Dad' },
  { value: 'grandma', label: 'Grandma' },
  { value: 'grandpa', label: 'Grandpa' },
  { value: 'partner', label: 'Partner' },
  { value: 'mother-in-law', label: 'Mother-in-law' },
  { value: 'father-in-law', label: 'Father-in-law' },
  { value: 'other', label: 'Other' },
] as const

const TECH_COMFORT_OPTIONS = [
  { value: 'low', label: 'Not very', desc: 'Needs extra patience and simpler explanations' },
  { value: 'medium', label: 'Getting there', desc: 'Can handle basics but appreciates guidance' },
  { value: 'high', label: 'Pretty confident', desc: 'Comfortable with technology, just needs awareness' },
] as const

type WizardStep = 'who' | 'tailor' | 'message'

const WIZARD_STEPS: WizardStep[] = ['who', 'tailor', 'message']

function GuardianPlanBuilderStep({
  guardianName,
  guardianAvatarUrl,
  familyMembers,
  setFamilyMembers,
  visualStyle,
  setVisualStyle,
  availableLicenses,
  onNext,
}: {
  guardianName: string
  guardianAvatarUrl?: string | null
  familyMembers: FamilyMember[]
  setFamilyMembers: React.Dispatch<React.SetStateAction<FamilyMember[]>>
  visualStyle: PlanVisualStyle
  setVisualStyle: (style: PlanVisualStyle) => void
  availableLicenses: number
  onNext: () => void
}) {
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null)
  const [wizardStep, setWizardStep] = useState<WizardStep>('who')
  const [hideAvatar, setHideAvatar] = useState(false)
  const [relationshipDropdownOpen, setRelationshipDropdownOpen] = useState(false)
  const remainingSlots = availableLicenses - familyMembers.length

  // Use avatar unless dev toggle is hiding it
  const effectiveAvatarUrl = hideAvatar ? undefined : (guardianAvatarUrl ?? undefined)

  const addFamilyMember = () => {
    if (remainingSlots <= 0) return
    const newMember: FamilyMember = {
      id: crypto.randomUUID(),
      email: '',
      name: '',
      relationship: '',
      techComfort: '',
      focusAreas: '',
      personalMessage: '',
      isExpanded: true,
    }
    setFamilyMembers((prev) => [...prev, newMember])
    openMemberModal(newMember.id)
  }

  const updateFamilyMember = (id: string, updates: Partial<FamilyMember>) => {
    setFamilyMembers((prev) =>
      prev.map((m) => (m.id === id ? { ...m, ...updates } : m))
    )
  }

  const removeFamilyMember = (id: string) => {
    setFamilyMembers((prev) => prev.filter((m) => m.id !== id))
    if (editingMemberId === id) setEditingMemberId(null)
  }

  const getRelationshipLabel = (rel: string) => {
    return RELATIONSHIP_OPTIONS.find((r) => r.value === rel)?.label || ''
  }


  const editingMember = familyMembers.find((m) => m.id === editingMemberId)

  const closeModal = () => {
    // Remove member if they haven't entered an email
    if (editingMember && !editingMember.email.trim()) {
      removeFamilyMember(editingMember.id)
    }
    setEditingMemberId(null)
    setWizardStep('who') // Reset wizard
    setRelationshipDropdownOpen(false)
  }

  const saveMember = () => {
    setEditingMemberId(null)
    setWizardStep('who') // Reset wizard
    setRelationshipDropdownOpen(false)
  }

  const openMemberModal = (memberId: string) => {
    setEditingMemberId(memberId)
    setWizardStep('who')
    setRelationshipDropdownOpen(false)
  }

  const wizardStepIndex = WIZARD_STEPS.indexOf(wizardStep)
  const isFirstStep = wizardStepIndex === 0
  const isLastStep = wizardStepIndex === WIZARD_STEPS.length - 1

  const goToNextWizardStep = () => {
    if (!isLastStep) {
      setWizardStep(WIZARD_STEPS[wizardStepIndex + 1])
    } else {
      saveMember()
    }
  }

  const goToPrevWizardStep = () => {
    if (!isFirstStep) {
      setWizardStep(WIZARD_STEPS[wizardStepIndex - 1])
    }
  }

  const canProceedFromCurrentStep = () => {
    if (!editingMember) return false
    if (wizardStep === 'who') {
      // Email is required
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(editingMember.email.trim())
    }
    return true // Other steps are optional
  }

  return (
    <>
      {/* Dev: Visual Style Toggle */}
      {import.meta.env.DEV && (
        <div className="fixed top-20 left-2 z-50 bg-indigo-900/90 text-white text-xs rounded-lg p-2 space-y-2">
          <div>
            <div className="text-[10px] text-indigo-300 mb-1">Visual Style:</div>
            <div className="flex gap-1">
              {(['nodes', 'cards', 'orbit'] as const).map((style) => (
                <button
                  key={style}
                  onClick={() => setVisualStyle(style)}
                  className={`px-2 py-1 rounded cursor-pointer transition-colors ${
                    visualStyle === style
                      ? 'bg-indigo-500 text-white'
                      : 'bg-indigo-800 text-indigo-300 hover:bg-indigo-700'
                  }`}
                >
                  {style}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-indigo-300 mb-1">Avatar:</div>
            <div className="flex gap-1">
              <button
                onClick={() => setHideAvatar(false)}
                className={`px-2 py-1 rounded cursor-pointer transition-colors ${
                  !hideAvatar
                    ? 'bg-indigo-500 text-white'
                    : 'bg-indigo-800 text-indigo-300 hover:bg-indigo-700'
                }`}
              >
                Show
              </button>
              <button
                onClick={() => setHideAvatar(true)}
                className={`px-2 py-1 rounded cursor-pointer transition-colors ${
                  hideAvatar
                    ? 'bg-indigo-500 text-white'
                    : 'bg-indigo-800 text-indigo-300 hover:bg-indigo-700'
                }`}
              >
                Hide
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Title and description */}
      <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight">
        Add your family
      </h1>

      <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
        Click on an empty slot to get started.
      </p>

      {/* ============================================================
          VISUAL OPTION A: Connected Nodes (Horizontal)
          ============================================================ */}
      {visualStyle === 'nodes' && (
        <div className="mb-10">
          <div className="flex items-start justify-center gap-6 flex-wrap max-w-4xl mx-auto">
            {/* Guardian Node (You) */}
            <div className="flex flex-col items-center">
              <div className="relative">
                {effectiveAvatarUrl ? (
                  <img
                    src={effectiveAvatarUrl}
                    alt={guardianName || 'You'}
                    className="w-36 h-36 rounded-full object-cover shadow-lg border-4 border-white"
                  />
                ) : (
                  <div className="w-36 h-36 rounded-full bg-gray-100 border-4 border-white flex items-center justify-center text-gray-600 font-bold text-4xl shadow-lg">
                    {guardianName ? guardianName.charAt(0).toUpperCase() : 'You'}
                  </div>
                )}
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-gray-700 text-white text-sm font-bold px-3 py-1 rounded-full whitespace-nowrap">
                  YOU
                </div>
              </div>
              <div className="mt-5 text-center">
                <div className="text-lg font-semibold text-gray-900 max-w-[120px] truncate">
                  {guardianName || 'You'}
                </div>
                <div className="text-base text-gray-500">Guardian</div>
              </div>
            </div>

            {/* Filled Member Nodes */}
            {familyMembers.map((member) => (
              <div key={member.id} className="flex flex-col items-center">
                <button
                  onClick={() => openMemberModal(member.id)}
                  className="w-36 h-36 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 text-4xl font-semibold shadow-lg border-4 border-white cursor-pointer hover:scale-105 transition-transform"
                >
                  {(member.name || getRelationshipLabel(member.relationship) || '?').charAt(0).toUpperCase()}
                </button>
                <div className="mt-5 text-center">
                  <div className="text-lg font-semibold text-gray-900 max-w-[120px] truncate">
                    {member.name || getRelationshipLabel(member.relationship) || 'New'}
                  </div>
                  {member.relationship && (
                    <div className="text-base text-gray-500 max-w-[120px] truncate">
                      {getRelationshipLabel(member.relationship)}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Empty Slot Nodes */}
            {Array.from({ length: remainingSlots }).map((_, i) => (
              <div key={`empty-${i}`} className="flex flex-col items-center">
                <button
                  onClick={addFamilyMember}
                  className="w-36 h-36 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-violet-400 hover:text-violet-500 hover:bg-violet-50 transition-all cursor-pointer"
                >
                  <Plus className="w-10 h-10" />
                </button>
                <div className="mt-5 text-center">
                  <div className="text-lg text-gray-400">Add</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============================================================
          VISUAL OPTION B: Plan Cards Row
          ============================================================ */}
      {visualStyle === 'cards' && (
        <div className="mb-10">
          <div className="flex gap-5 justify-center flex-wrap max-w-3xl mx-auto">
            {/* Guardian Card */}
            <div className="w-40 h-52 rounded-3xl bg-white border-2 border-gray-200 p-5 flex flex-col items-center justify-center shadow-sm">
              {effectiveAvatarUrl ? (
                <img
                  src={effectiveAvatarUrl}
                  alt={guardianName || 'You'}
                  className="w-16 h-16 rounded-full object-cover mb-4 border-2 border-gray-100"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-2xl font-bold mb-4 text-gray-600">
                  {guardianName ? guardianName.charAt(0).toUpperCase() : '👤'}
                </div>
              )}
              <div className="text-lg font-semibold truncate max-w-full text-center text-gray-900">
                {guardianName || 'You'}
              </div>
              <div className="text-sm text-gray-500 mt-1">Guardian</div>
            </div>

            {/* Filled Member Cards */}
            {familyMembers.map((member) => (
              <button
                key={member.id}
                onClick={() => openMemberModal(member.id)}
                className="w-40 h-52 rounded-3xl bg-white border-2 border-gray-200 p-5 flex flex-col items-center justify-center shadow-sm cursor-pointer hover:scale-105 hover:border-violet-300 transition-all"
              >
                <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 text-3xl mb-4">
                  {(member.name || getRelationshipLabel(member.relationship) || '?').charAt(0).toUpperCase()}
                </div>
                <div className="text-lg font-semibold truncate max-w-full text-center text-gray-900">
                  {member.name || getRelationshipLabel(member.relationship) || 'New'}
                </div>
                <div className="text-sm text-gray-500 mt-1 truncate max-w-full">
                  {getRelationshipLabel(member.relationship) || 'Family'}
                </div>
              </button>
            ))}

            {/* Empty Slot Cards */}
            {Array.from({ length: remainingSlots }).map((_, i) => (
              <button
                key={`empty-${i}`}
                onClick={addFamilyMember}
                className="w-40 h-52 rounded-3xl border-2 border-dashed border-gray-300 p-5 flex flex-col items-center justify-center text-gray-400 hover:border-violet-400 hover:text-violet-500 hover:bg-violet-50 transition-all cursor-pointer"
              >
                <div className="w-16 h-16 rounded-full border-2 border-dashed border-current flex items-center justify-center mb-4">
                  <Plus className="w-7 h-7" />
                </div>
                <div className="text-lg font-medium">Add</div>
                <div className="text-sm">Tap to invite</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ============================================================
          VISUAL OPTION C: Orbit View
          ============================================================ */}
      {visualStyle === 'orbit' && (
        <div className="mb-10">
          <div className="relative w-80 h-80 mx-auto">
            {/* Orbit Ring */}
            <div className="absolute inset-6 rounded-full border-2 border-dashed border-gray-200"></div>

            {/* Guardian at Center */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
              {effectiveAvatarUrl ? (
                <img
                  src={effectiveAvatarUrl}
                  alt={guardianName || 'You'}
                  className="w-24 h-24 rounded-full object-cover shadow-xl border-4 border-white"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gray-100 border-4 border-white flex items-center justify-center text-gray-600 font-bold text-2xl shadow-xl">
                  {guardianName ? guardianName.charAt(0).toUpperCase() : 'You'}
                </div>
              )}
            </div>

            {/* Orbiting Members */}
            {familyMembers.map((member, index) => {
              const totalSlots = familyMembers.length + remainingSlots
              const angle = (index / totalSlots) * 2 * Math.PI - Math.PI / 2
              const radius = 110
              const x = Math.cos(angle) * radius
              const y = Math.sin(angle) * radius

              return (
                <div
                  key={member.id}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                  style={{ transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))` }}
                >
                  <button
                    onClick={() => openMemberModal(member.id)}
                    className="relative group cursor-pointer"
                  >
                    <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 text-2xl shadow-md border-3 border-white hover:scale-110 transition-transform">
                      {(member.name || getRelationshipLabel(member.relationship) || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-medium text-gray-700 whitespace-nowrap">
                      {member.name || getRelationshipLabel(member.relationship) || 'New'}
                    </div>
                  </button>
                </div>
              )
            })}

            {/* Empty Orbit Slots */}
            {Array.from({ length: remainingSlots }).map((_, i) => {
              const totalSlots = familyMembers.length + remainingSlots
              const index = familyMembers.length + i
              const angle = (index / totalSlots) * 2 * Math.PI - Math.PI / 2
              const radius = 110
              const x = Math.cos(angle) * radius
              const y = Math.sin(angle) * radius

              return (
                <div
                  key={`empty-${i}`}
                  className="absolute top-1/2 left-1/2"
                  style={{ transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))` }}
                >
                  <button
                    onClick={addFamilyMember}
                    className="w-16 h-16 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-violet-400 hover:text-violet-500 hover:bg-violet-50 transition-all cursor-pointer bg-white"
                  >
                    <Plus className="w-6 h-6" />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Summary / Reassurance */}
      {/* {familyMembers.length > 0 && familyMembers.some(m => m.email) && (
        <p className="text-base text-gray-500 max-w-md mx-auto mb-8 flex items-center justify-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          We'll email them an invite and help them get started.
        </p>
      )} */}

      {/* Continue Button */}
      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-lg hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
      >
        {familyMembers.filter(m => m.email).length > 0
          ? 'Continue'
          : "I'll add family later"
        }
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* ============================================================
          MODAL: Family Member Wizard
          ============================================================ */}
      <AnimatePresence>
        {editingMember && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-3xl max-w-xl w-full shadow-2xl overflow-hidden text-left"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Wizard Progress */}
              <div className="px-8 pt-8 pb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {WIZARD_STEPS.map((step, i) => (
                      <div key={step} className="flex items-center">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-bold transition-all ${
                            i < wizardStepIndex
                              ? 'bg-violet-600 text-white'
                              : i === wizardStepIndex
                              ? 'bg-violet-600 text-white'
                              : 'bg-gray-100 text-gray-400'
                          }`}
                        >
                          {i < wizardStepIndex ? '✓' : i + 1}
                        </div>
                        {i < WIZARD_STEPS.length - 1 && (
                          <div
                            className={`w-10 h-0.5 mx-1.5 ${
                              i < wizardStepIndex ? 'bg-violet-600' : 'bg-gray-200'
                            }`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={closeModal}
                    className="p-2 text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-gray-100"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Wizard Content */}
              <div className="px-8 pb-8">
                <AnimatePresence mode="wait">
                  {/* ========== STEP 1: Who ========== */}
                  {wizardStep === 'who' && (
                    <motion.div
                      key="who"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="mb-8">
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">
                          Who would you like to add?
                        </h2>
                        <p className="text-lg text-gray-500">
                          We'll send them an invitation to join your family plan.
                        </p>
                      </div>

                      <div className="space-y-6">
                        {/* Email */}
                        <div>
                          <label className="block text-base font-medium text-gray-700 mb-2">
                            Their email address <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            value={editingMember.email}
                            onChange={(e) => updateFamilyMember(editingMember.id, { email: e.target.value })}
                            placeholder="e.g. mom@email.com"
                            className="w-full px-5 py-4 border border-gray-200 rounded-xl focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 text-lg"
                            autoFocus
                          />
                        </div>

                        {/* Name + Relationship */}
                        <div className="grid grid-cols-2 gap-5">
                          <div>
                            <label className="block text-base font-medium text-gray-700 mb-2">
                              First name
                            </label>
                            <input
                              type="text"
                              value={editingMember.name}
                              onChange={(e) => updateFamilyMember(editingMember.id, { name: e.target.value })}
                              placeholder="e.g. Margaret"
                              className="w-full px-5 py-4 border border-gray-200 rounded-xl focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 text-lg"
                            />
                          </div>
                          <div className="relative">
                            <label className="flex items-center gap-1.5 text-base font-medium text-gray-700 mb-2">
                              Relationship
                              <div className="relative group">
                                <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                                <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible w-64 text-left z-[80]">
                                  Knowing your relationship helps us understand your family dynamic so we can tailor guidance and assign activities that make our support more effective.
                                  <div className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-gray-900" />
                                </div>
                              </div>
                            </label>
                            <button
                              type="button"
                              onClick={() => setRelationshipDropdownOpen(!relationshipDropdownOpen)}
                              className="w-full px-5 py-4 border border-gray-200 rounded-xl bg-white text-lg text-left flex items-center justify-between cursor-pointer hover:border-gray-300 focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                            >
                              {editingMember.relationship ? (
                                <span>{getRelationshipLabel(editingMember.relationship)}</span>
                              ) : (
                                <span className="text-gray-400">Select...</span>
                              )}
                              <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${relationshipDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {relationshipDropdownOpen && (
                              <>
                                <div
                                  className="fixed inset-0 z-[60]"
                                  onClick={() => setRelationshipDropdownOpen(false)}
                                />
                                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-xl shadow-lg z-[70] py-2 max-h-64 overflow-auto">
                                  {RELATIONSHIP_OPTIONS.map((opt) => (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      onClick={() => {
                                        updateFamilyMember(editingMember.id, { relationship: opt.value as FamilyMember['relationship'] })
                                        setRelationshipDropdownOpen(false)
                                      }}
                                      className={`w-full px-5 py-3 text-left hover:bg-gray-50 cursor-pointer text-lg ${
                                        editingMember.relationship === opt.value ? 'bg-violet-50 text-violet-700' : 'text-gray-700'
                                      }`}
                                    >
                                      {opt.label}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* ========== STEP 2: Tailor ========== */}
                  {wizardStep === 'tailor' && (
                    <motion.div
                      key="tailor"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="mb-8">
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">
                          Help us support {editingMember.name || 'them'}
                        </h2>
                        <p className="text-lg text-gray-500">
                          This helps us tailor their training to their needs. <span className="text-gray-400">(Optional)</span>
                        </p>
                      </div>

                      <div className="space-y-8">
                        {/* Tech Comfort */}
                        <div>
                          <label className="block text-lg font-medium text-gray-700 mb-3">
                            How comfortable are they with technology?
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {TECH_COMFORT_OPTIONS.map((opt) => {
                              const isSelected = editingMember.techComfort === opt.value
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => updateFamilyMember(editingMember.id, { techComfort: opt.value as FamilyMember['techComfort'] })}
                                  className={`p-4 rounded-xl text-center cursor-pointer border-2 ${
                                    isSelected
                                      ? 'bg-violet-50 border-violet-300'
                                      : 'bg-gray-50 border-gray-200'
                                  }`}
                                >
                                  <div className="font-semibold text-gray-900 text-base">{opt.label}</div>
                                </button>
                              )
                            })}
                          </div>
                        </div>

                        {/* Focus Areas */}
                        <div>
                          <label className="block text-lg font-medium text-gray-700 mb-3">
                            Any security topics or scam types to focus on for {editingMember.name || 'them'}?
                          </label>
                          <input
                            type="text"
                            value={editingMember.focusAreas}
                            onChange={(e) => updateFamilyMember(editingMember.id, { focusAreas: e.target.value })}
                            placeholder="e.g. phishing, online shopping, romance scams..."
                            className="w-full px-5 py-4 border border-gray-200 rounded-xl focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 text-lg"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* ========== STEP 3: Message ========== */}
                  {wizardStep === 'message' && (
                    <motion.div
                      key="message"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="mb-8">
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">
                          Add a personal touch
                        </h2>
                        <p className="text-lg text-gray-500">
                          Include a message with their invitation. <span className="text-gray-400">(Optional)</span>
                        </p>
                      </div>

                      <div>
                        <textarea
                          value={editingMember.personalMessage}
                          onChange={(e) => updateFamilyMember(editingMember.id, { personalMessage: e.target.value })}
                          placeholder={`e.g. Hi ${editingMember.name || 'Mom'}, I signed us up for this together. It's really helpful - give it a go!`}
                          rows={5}
                          className="w-full px-5 py-4 border border-gray-200 rounded-xl focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 resize-none text-lg"
                          autoFocus
                        />
                        <p className="text-sm text-gray-400 mt-3">
                          This message will appear in their invitation email.
                        </p>
                      </div>

                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Wizard Footer */}
              <div className="px-8 py-6 border-t border-gray-100 flex items-center justify-between bg-gray-50">
                <div>
                  {isFirstStep ? (
                    <button
                      onClick={() => removeFamilyMember(editingMember.id)}
                      className="text-red-500 font-medium hover:text-red-600 cursor-pointer flex items-center gap-2 text-base"
                    >
                      <Trash2 className="w-5 h-5" />
                      Remove
                    </button>
                  ) : (
                    <button
                      onClick={goToPrevWizardStep}
                      className="text-gray-600 font-medium hover:text-gray-800 cursor-pointer text-base"
                    >
                      ← Back
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-4">
                  {!isFirstStep && !isLastStep && (
                    <button
                      onClick={saveMember}
                      className="text-gray-500 font-medium hover:text-gray-700 cursor-pointer text-base"
                    >
                      Skip
                    </button>
                  )}
                  <button
                    onClick={goToNextWizardStep}
                    disabled={!canProceedFromCurrentStep()}
                    className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-8 py-4 rounded-full text-lg hover:bg-violet-700 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLastStep ? 'Done' : 'Continue'}
                    {!isLastStep && <ChevronRight className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

function GuardianInviteStep({
  inviteEmail,
  setInviteEmail,
  invitedEmails,
  inviteError,
  isLoading,
  onSendInvitation,
  onNext,
  availableLicenses,
}: {
  inviteEmail: string
  setInviteEmail: (email: string) => void
  invitedEmails: string[]
  inviteError: string | null
  isLoading: boolean
  onSendInvitation: () => void
  onNext: () => void
  availableLicenses: number
}) {
  const remainingLicenses = availableLicenses - invitedEmails.length

  return (
    <>
      <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-[24px] flex items-center justify-center mb-8 mx-auto border border-blue-200/50">
        <UserPlus className="w-10 h-10 text-blue-500" />
      </div>
      <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-4">
        Build Your Circle
      </p>
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
        Add family members<br />to your family plan
      </h1>
      <p className="text-gray-600 mb-6 text-lg">
        You have {remainingLicenses} safety link{remainingLicenses !== 1 ? 's' : ''} left to share.
      </p>

      {invitedEmails.length > 0 && (
        <div className="mb-6 p-4 bg-emerald-50 rounded-xl border border-emerald-100 max-w-sm mx-auto">
          <p className="text-sm font-semibold text-emerald-800 mb-2">
            Safety links shared:
          </p>
          <ul className="text-sm text-emerald-700 space-y-1">
            {invitedEmails.map((email, i) => (
              <li key={i} className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                {email}
              </li>
            ))}
          </ul>
        </div>
      )}

      {remainingLicenses > 0 && (
        <form className="max-w-sm mx-auto mb-6" onSubmit={(e) => { e.preventDefault(); if (inviteEmail.trim() && !isLoading) onSendInvitation(); }}>
          <div className="flex gap-2">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Enter email address"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-200"
            />
            <button
              type="submit"
              disabled={!inviteEmail.trim() || isLoading}
              className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isLoading ? '...' : 'Share Link'}
            </button>
          </div>
          {inviteError && (
            <p className="text-red-600 text-sm mt-2">{inviteError}</p>
          )}
        </form>
      )}

      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-[#1d1d1f] text-white font-bold px-12 py-5 rounded-full hover:bg-black transition-all cursor-pointer mt-4 shadow-xl hover:scale-[1.02] active:scale-[0.98]"
      >
        {invitedEmails.length > 0 ? 'Continue' : "I'll share later"} <ChevronRight className="w-5 h-5 ml-2" />
      </button>
    </>
  )
}

function GuardianExploreOfferStep({
  onExplore,
  onSkip,
}: {
  onExplore: () => void
  onSkip: () => void
}) {
  return (
    <>
      <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-orange-100 rounded-[24px] flex items-center justify-center mb-8 mx-auto border border-amber-200/50">
        <BookOpen className="w-10 h-10 text-amber-500" />
      </div>
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
        While you wait...
      </h1>
      <p className="text-xl text-gray-600 mb-8 max-w-md mx-auto">
        Would you like to preview what your family will learn? It's the same training - and it'll help you support them.
      </p>
      <div className="flex flex-col gap-3 max-w-sm mx-auto">
        <button
          onClick={onExplore}
          className="inline-flex items-center justify-center gap-2 bg-[#1d1d1f] text-white font-bold px-12 py-5 rounded-full hover:bg-black transition-all cursor-pointer shadow-xl hover:scale-[1.02] active:scale-[0.98]"
        >
          Yes, show me <ArrowRight className="w-5 h-5 ml-2" />
        </button>
        <button
          onClick={onSkip}
          className="text-gray-500 text-sm font-medium hover:text-violet-600 transition-colors cursor-pointer py-2"
        >
          Skip to dashboard
        </button>
      </div>
    </>
  )
}

function GuardianFamilySummaryStep({
  familyMembers,
  hasWelcomeKit,
  selectedAddress,
  onNext,
}: {
  familyMembers: FamilyMember[]
  hasWelcomeKit: boolean
  selectedAddress: { address: string; city: string; postcode: string } | null
  onNext: () => void
}) {
  const addedMembers = familyMembers.filter(m => m.email && m.name)

  // Format family member names for display
  const memberNames = addedMembers.length === 0
    ? ''
    : addedMembers.length === 1
      ? addedMembers[0].name
      : addedMembers.length === 2
        ? `${addedMembers[0].name} and ${addedMembers[1].name}`
        : `${addedMembers.slice(0, -1).map(m => m.name).join(', ')}, and ${addedMembers[addedMembers.length - 1].name}`

  // Format address for display (city + postcode)
  const addressDisplay = selectedAddress
    ? `${selectedAddress.city}, ${selectedAddress.postcode}`
    : null

  return (
    <>
      {/* Animated Draw-in Check */}
      <svg
        className="w-24 h-24 mx-auto mb-8"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Circle - draws first */}
        <motion.circle
          cx="12"
          cy="12"
          r="10"
          className="text-emerald-500"
          stroke="currentColor"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
        {/* Checkmark - draws after circle */}
        <motion.path
          d="M9 12l2 2 4-4"
          className="text-emerald-500"
          stroke="currentColor"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.4 }}
        />
      </svg>
      <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight">
        Your family plan is ready
      </h1>

      {/* Checklist of what happens next */}
      <div className="max-w-2xl mx-auto mb-10 text-left">
        {/* <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4 text-center">
          Here's what happens next
        </p> */}
        <div className="space-y-4">
          {hasWelcomeKit && addressDisplay && (
            <motion.div
              className="flex items-center gap-3"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <CheckCircle2 className="w-7 h-7 text-emerald-500 flex-shrink-0" />
              <p className="text-lg sm:text-xl md:text-2xl text-gray-700">
                Welcome Kit shipping to <span className="font-semibold">{addressDisplay}</span>
              </p>
            </motion.div>
          )}
          <motion.div
            className="flex items-start gap-3"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: hasWelcomeKit ? 0.7 : 0.5 }}
          >
            <CheckCircle2 className="w-7 h-7 text-emerald-500 flex-shrink-0 mt-0.5" />
            <p className="text-lg sm:text-xl md:text-2xl text-gray-700">
              {addedMembers.length > 0 ? (
                <><span className="font-semibold">{memberNames}</span> will receive {addedMembers.length === 1 ? 'a personalised invite' : 'personalised invites'}</>
              ) : (
                <> Invite family members to your plan</>
              )}
            </p>
          </motion.div>
          <motion.div
            className="flex items-start gap-3"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: hasWelcomeKit ? 0.9 : 0.7 }}
          >
            <CheckCircle2 className="w-7 h-7 text-emerald-500 flex-shrink-0 mt-0.5" />
            <p className="text-lg sm:text-xl md:text-2xl text-gray-700">
              We'll handle their setup, training, and ongoing support. 
              {/* We'll take it from here: personalised training, helpful tips, and support whenever they need it    */}
            </p>
          </motion.div>
          <motion.div
            className="flex items-start gap-3"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: hasWelcomeKit ? 1.1 : 0.9 }}
          >
            <CheckCircle2 className="w-7 h-7 text-emerald-500 flex-shrink-0 mt-0.5" />
            <p className="text-lg sm:text-xl md:text-2xl text-gray-700">
              You can check their progress anytime in your dashboard
            </p>
          </motion.div>
        </div>
      </div>

      <motion.p
        className="text-xl md:text-2xl text-gray-600 mb-10 max-w-xl mx-auto leading-relaxed"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3 }}
      >
        Now, let's talk about <span className="font-bold italic">your</span> role. 
      </motion.p>

      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-lg hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
      >
        Continue <ChevronRight className="w-5 h-5" />
      </button>
    </>
  )
}

function GuardianWhyYouMatterStep({
  onNext,
  userAvatarUrl,
  userName,
}: {
  onNext: () => void
  userAvatarUrl?: string | null
  userName?: string
}) {
  return (
    <>
      {/* Partnership Visual - Overlapping Circles */}
      <div className="flex items-center justify-center mb-8">
        {/* GranGuard "G" circle */}
        <div className="w-32 h-32 rounded-full bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center shadow-lg border-4 border-white z-10">
          <img src={gWhiteLogo} alt="GranGuard" className="w-16 h-16 object-contain" />
        </div>
        {/* User's profile circle - overlapping */}
        <div className="-ml-8 z-20 relative">
          {userAvatarUrl ? (
            <img
              src={userAvatarUrl}
              alt={userName || 'You'}
              className="w-32 h-32 rounded-full object-cover shadow-lg border-4 border-white"
            />
          ) : (
            <div className="w-32 h-32 rounded-full bg-gray-200 border-4 border-white flex items-center justify-center text-gray-600 font-bold text-4xl shadow-lg">
              {userName ? userName.charAt(0).toUpperCase() : 'Y'}
            </div>
          )}
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-gray-700 text-white text-sm font-bold px-3 py-1 rounded-full whitespace-nowrap">
            YOU
          </div>
        </div>
      </div>

      <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
        Our Shared Mission
      </h1>

      <p className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
        We'll be working together to keep your family safe.
        <br /><br />
        We'll handle the training, alerts, and support. But we'll need your help to encourage small wins and celebrate their progress.
      </p>

      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-lg hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
      >
        I understand <ChevronRight className="w-5 h-5" />
      </button>
    </>
  )
}

type NextStepOption = 'breach_scan' | 'scam_library' | 'family_tips'

const NEXT_STEP_OPTIONS: Array<{
  id: NextStepOption
  icon: React.ReactNode
  title: string
  description: string
}> = [
  {
    id: 'breach_scan',
    icon: <Search className="w-6 h-6 text-violet-600" />,
    title: 'Check your data exposure',
    description: 'See if your personal info has been leaked',
  },
  {
    id: 'scam_library',
    icon: <ShieldAlert className="w-6 h-6 text-amber-600" />,
    title: 'Explore the scam library',
    description: 'Learn about the latest threats',
  },
  {
    id: 'family_tips',
    icon: <MessageSquare className="w-6 h-6 text-emerald-600" />,
    title: 'Tips for family conversations',
    description: 'How to talk about security without causing alarm',
  },
]

function GuardianReadyStep({
  onGoToDashboard,
}: {
  onGoToDashboard: () => void
}) {
  const [selectedOptions, setSelectedOptions] = useState<NextStepOption[]>([])

  const toggleOption = (id: NextStepOption) => {
    setSelectedOptions((prev) =>
      prev.includes(id) ? prev.filter((o) => o !== id) : [...prev, id]
    )
  }

  return (
    <>
      {/* Celebration icon */}
      <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-full flex items-center justify-center mb-8 mx-auto">
        <CheckCircle2 className="w-10 h-10 text-emerald-600" />
      </div>

      <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-4 leading-tight">
        You're all set!
      </h1>

      <p className="text-xl text-gray-600 mb-10 max-w-lg mx-auto">
        Your family plan is ready to go. What would you like to explore first?
      </p>

      {/* Optional next steps */}
      <div className="max-w-md mx-auto mb-10 space-y-3">
        {NEXT_STEP_OPTIONS.map((option) => {
          const isSelected = selectedOptions.includes(option.id)
          return (
            <button
              key={option.id}
              onClick={() => toggleOption(option.id)}
              className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all cursor-pointer text-left ${
                isSelected
                  ? 'border-violet-500 bg-violet-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                isSelected ? 'bg-violet-100' : 'bg-gray-100'
              }`}>
                {option.icon}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">{option.title}</p>
                <p className="text-sm text-gray-500">{option.description}</p>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                isSelected
                  ? 'border-violet-500 bg-violet-500'
                  : 'border-gray-300'
              }`}>
                {isSelected && <Check className="w-4 h-4 text-white" />}
              </div>
            </button>
          )
        })}
      </div>

      {/* CTAs */}
      <div className="flex flex-col items-center gap-4">
        <button
          onClick={onGoToDashboard}
          className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-lg hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
        >
          Go to dashboard <ChevronRight className="w-5 h-5" />
        </button>

        {selectedOptions.length > 0 && (
          <p className="text-sm text-gray-500">
            We'll highlight these for you in your dashboard
          </p>
        )}
      </div>
    </>
  )
}

function GuardianBreachScanStep({ onNext }: { onNext: () => void }) {
  const [isEnabled, setIsEnabled] = useState(false)
  const [isScanning, setIsScanning] = useState(false)
  const [scanComplete, setScanComplete] = useState(false)
  const [breachCount, setBreachCount] = useState(0)

  const handleToggle = () => {
    if (isEnabled) return // Can't turn off once on

    setIsEnabled(true)
    setIsScanning(true)

    // Simulate scan with random breach count
    setTimeout(() => {
      setIsScanning(false)
      setScanComplete(true)
      setBreachCount(Math.floor(Math.random() * 5) + 2) // 2-6 breaches
    }, 3000)
  }

  return (
    <>
      <div className="w-20 h-20 bg-gradient-to-br from-violet-100 to-indigo-100 rounded-[24px] flex items-center justify-center mb-8 mx-auto border border-violet-200/50">
        <Search className="w-10 h-10 text-violet-500" />
      </div>

      {!scanComplete ? (
        <>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight">
            Enable breach monitoring
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            Want us to scan the dark web for your personal data?
          </p>

          {/* Toggle Switch */}
          <div className="flex flex-col items-center gap-6 mb-10">
            <button
              onClick={handleToggle}
              disabled={isScanning}
              className={`relative w-24 h-12 rounded-full transition-all duration-300 cursor-pointer ${
                isEnabled ? 'bg-violet-600' : 'bg-gray-300'
              }`}
            >
              <div
                className={`absolute top-1 w-10 h-10 bg-white rounded-full shadow-md transition-all duration-300 ${
                  isEnabled ? 'left-[52px]' : 'left-1'
                }`}
              />
            </button>
            <p className="text-sm text-gray-500">
              {isScanning ? 'Scanning...' : isEnabled ? 'Monitoring active' : 'Tap to enable'}
            </p>
          </div>

          {/* Scanning Animation */}
          {isScanning && (
            <div className="flex flex-col items-center gap-4">
              <div className="relative w-32 h-32">
                <div className="absolute inset-0 rounded-full border-4 border-violet-200 animate-ping" />
                <div className="absolute inset-2 rounded-full border-4 border-violet-300 animate-ping" style={{ animationDelay: '0.2s' }} />
                <div className="absolute inset-4 rounded-full border-4 border-violet-400 animate-ping" style={{ animationDelay: '0.4s' }} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Search className="w-8 h-8 text-violet-600 animate-pulse" />
                </div>
              </div>
              <p className="text-gray-600 animate-pulse">Scanning 12,000+ breach databases...</p>
            </div>
          )}
        </>
      ) : (
        <>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight">
            Monitoring activated
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            We found your data in <span className="font-bold text-red-600">{breachCount} breaches</span>.
            We'll alert you to any new exposures.
          </p>

          <div className="bg-amber-50 rounded-2xl p-5 max-w-md mx-auto mb-10 border border-amber-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-amber-900">Your data is out there</p>
                <p className="text-sm text-amber-700">We'll show you how to protect yourself</p>
              </div>
            </div>
          </div>

          <button
            onClick={onNext}
            className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-lg hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
          >
            Continue <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}
    </>
  )
}

// ==============================================================================
// SPONSOR POST-SETUP STEP COMPONENTS
// ==============================================================================

// Trending scam examples by market (fallbacks if API unavailable)
const TRENDING_SCAMS: Record<string, { headline: string; stat: string; timeframe: string }> = {
  GB: {
    headline: '"HMRC" texts claiming you\'re owed a refund',
    stat: '2.3M UK phones',
    timeframe: 'last month',
  },
  US: {
    headline: '"IRS" texts about unclaimed tax refunds',
    stat: '4.1M US phones',
    timeframe: 'last month',
  },
  CA: {
    headline: '"CRA" calls threatening arrest for unpaid taxes',
    stat: '890K Canadian phones',
    timeframe: 'last month',
  },
  AU: {
    headline: '"ATO" messages about tax debt collection',
    stat: '1.2M Australian phones',
    timeframe: 'last month',
  },
}

function SponsorYourRoleStep({
  userMarket,
  onNext,
}: {
  userMarket: string
  onNext: () => void
}) {
  const trendingScam = TRENDING_SCAMS[userMarket] || TRENDING_SCAMS.GB

  return (
    <>
      <div className="w-20 h-20 bg-gradient-to-br from-violet-100 to-indigo-100 rounded-[24px] flex items-center justify-center mb-8 mx-auto border border-violet-200/50">
        <Home className="w-10 h-10 text-violet-500" />
      </div>

      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
        You're the person they'll call
      </h1>
      <p className="text-xl text-gray-600 mb-8 max-w-lg mx-auto">
        When something looks suspicious, you're who your family turns to. Here's what you can do:
      </p>

      {/* Benefits List */}
      <div className="max-w-md mx-auto mb-8 bg-gray-50 rounded-2xl border border-gray-100 divide-y divide-gray-100">
        <div className="flex items-center gap-4 p-4">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Link className="w-5 h-5 text-blue-600" />
          </div>
          <span className="text-gray-900 font-medium">Check suspicious links in seconds</span>
        </div>
        <div className="flex items-center gap-4 p-4">
          <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-5 h-5 text-violet-600" />
          </div>
          <span className="text-gray-900 font-medium">Know the latest scam tactics</span>
        </div>
        <div className="flex items-center gap-4 p-4">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-amber-600" />
          </div>
          <span className="text-gray-900 font-medium">Get alerts when new threats emerge</span>
        </div>
      </div>

      {/* Trending Scam Card */}
      <div className="max-w-md mx-auto mb-10 bg-amber-50 rounded-2xl border border-amber-200 p-5">
        <p className="text-xs font-bold uppercase tracking-widest text-amber-700 mb-2">
          Trending Now
        </p>
        <p className="text-gray-900 font-medium mb-1">
          {trendingScam.headline}
        </p>
        <p className="text-sm text-amber-700">
          Hit {trendingScam.stat} {trendingScam.timeframe}
        </p>
      </div>

      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-lg hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
      >
        Got it <ChevronRight className="w-5 h-5" />
      </button>
    </>
  )
}

const CONCERN_OPTIONS: Array<{
  type: SponsorConcern
  icon: React.ReactNode
  title: string
  description: string
}> = [
  {
    type: 'family_communication',
    icon: <MessageSquare className="w-6 h-6 text-emerald-600" />,
    title: 'Knowing what to say',
    description: 'when family asks about something suspicious',
  },
  {
    type: 'link_checking',
    icon: <Link className="w-6 h-6 text-blue-600" />,
    title: 'Quickly checking',
    description: 'if a link or message is safe',
  },
  {
    type: 'scam_awareness',
    icon: <Smartphone className="w-6 h-6 text-violet-600" />,
    title: 'Staying ahead',
    description: 'of the latest scam tactics',
  },
  {
    type: 'financial_protection',
    icon: <Building className="w-6 h-6 text-amber-600" />,
    title: 'Protecting',
    description: 'financial accounts and passwords',
  },
]

function SponsorConcernProbeStep({
  selectedConcerns,
  setSelectedConcerns,
  onNext,
  onSkip,
}: {
  selectedConcerns: SponsorConcern[]
  setSelectedConcerns: React.Dispatch<React.SetStateAction<SponsorConcern[]>>
  onNext: () => void
  onSkip: () => void
}) {
  const toggleConcern = (type: SponsorConcern) => {
    setSelectedConcerns((prev) =>
      prev.includes(type) ? prev.filter((c) => c !== type) : [...prev, type]
    )
  }

  return (
    <>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-2 leading-tight">
        What would help you most?
      </h1>
      <p className="text-lg text-gray-500 mb-8">Select any that apply</p>

      {/* Options Grid */}
      <div className="max-w-md mx-auto mb-8 space-y-3">
        {CONCERN_OPTIONS.map((option) => {
          const isSelected = selectedConcerns.includes(option.type)
          return (
            <button
              key={option.type}
              onClick={() => toggleConcern(option.type)}
              className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left cursor-pointer ${
                isSelected
                  ? 'border-violet-500 bg-violet-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  isSelected ? 'bg-violet-100' : 'bg-gray-100'
                }`}
              >
                {option.icon}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">{option.title}</p>
                <p className="text-sm text-gray-600">{option.description}</p>
              </div>
              {isSelected && (
                <CheckCircle2 className="w-6 h-6 text-violet-600 flex-shrink-0" />
              )}
            </button>
          )
        })}
      </div>

      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-lg hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200 mb-4"
      >
        Continue <ChevronRight className="w-5 h-5" />
      </button>

      <div>
        <button
          onClick={onSkip}
          className="text-gray-500 hover:text-gray-700 text-sm underline cursor-pointer"
        >
          Show me everything
        </button>
      </div>
    </>
  )
}

// ==============================================================================
// SPONSOR BREACH ACTIVATION STEP COMPONENTS
// ==============================================================================

function SponsorBridgeStep({
  onNext,
  familyMemberNames,
  userAvatarUrl,
  userName,
}: {
  onNext: () => void
  familyMemberNames: string[]
  userAvatarUrl?: string | null
  userName?: string
}) {
  const hasNames = familyMemberNames.length > 0

  return (
    <>
      {/* Orbiting protection graphic */}
      <div className="mb-10 flex justify-center">
        <div className="relative w-48 h-48">
          {/* Orbit ring */}
          <div className="absolute inset-0 rounded-full border-2 border-dashed border-violet-200" />

          {/* User profile - centered */}
          <div className="absolute inset-0 flex items-center justify-center">
            {userAvatarUrl ? (
              <img
                src={userAvatarUrl}
                alt={userName || 'You'}
                className="w-24 h-24 rounded-full object-cover shadow-lg border-4 border-white"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gray-200 border-4 border-white flex items-center justify-center text-gray-600 font-bold text-3xl shadow-lg">
                {userName ? userName.charAt(0).toUpperCase() : 'Y'}
              </div>
            )}
          </div>

          {/* Orbiting GranGuard logos - G stays upright */}
          {[0, 120, 240].map((deg) => {
            const r = 100
            return (
              <motion.div
                key={deg}
                className="absolute w-11 h-11 -ml-[22px] -mt-[22px] rounded-full bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center shadow-lg border-2 border-white"
                style={{ top: '50%', left: '50%' }}
                animate={{
                  x: Array.from({ length: 61 }, (_, i) =>
                    Math.cos(((deg + i * 6) * Math.PI) / 180) * r
                  ),
                  y: Array.from({ length: 61 }, (_, i) =>
                    Math.sin(((deg + i * 6) * Math.PI) / 180) * r
                  ),
                }}
                transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
              >
                <img src={gWhiteLogo} alt="" className="w-5 h-5 object-contain" />
              </motion.div>
            )
          })}
        </div>
      </div>

      <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
        We look out for you, too
      </h1>

      <p className="text-lg sm:text-xl md:text-2xl text-gray-500 mb-12 max-w-2xl mx-auto leading-relaxed">
        {hasNames
          ? <>Supporting {familyMemberNames.join(', ')} is just one part of your plan. You</>
          : <>Supporting your family is just one part of your plan. You</>
        } also get breach monitoring, link checking, and scam defense tools for
        yourself. Let&apos;s get your own protection set up.
      </p>

      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-lg hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
      >
        Show me <ChevronRight className="w-5 h-5" />
      </button>
    </>
  )
}

function SponsorScamTransitionStep({ onNext, breachCount }: { onNext: () => void; breachCount: number }) {
  return (
    <>
      <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-4 leading-tight">
        What this means for you
      </h1>
      <p className="text-lg sm:text-xl md:text-2xl text-gray-500 mb-12 max-w-2xl mx-auto">
        {breachCount > 0 ? (
          <>
            Scammers may use your personal details to pose as people you trust, including banks, companies, even family members.
          </>
        ) : (
          <>
            A clean scan is a great start, but scammers don't always need stolen data.
            They rely on urgency, impersonation, and emotional pressure to catch
            anyone off guard.
          </>
        )}
      </p>

      <div className="max-w-2xl mx-auto mb-14 text-left bg-gray-50/60 rounded-2xl p-6 flex items-start gap-5 shadow-sm">
        <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center shadow-md">
          <img src={gWhiteLogo} alt="GranGuard" className="w-7 h-7 object-contain" />
        </div>
        <div>
          <p className="text-base font-bold uppercase text-violet-500 mb-0">
            Key Takeaway
          </p>
          <p className="text-2xl font-medium text-gray-900 leading-snug">
            Trust must ALWAYS be verified, never assumed.
          </p>
        </div>
      </div>

      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-xl hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
      >
        See an example <ChevronRight className="w-5 h-5" />
      </button>
    </>
  )
}

function SponsorResolutionStep({
  onStartTour,
  isSubmitting,
  breachCount,
}: {
  onStartTour: () => void
  isSubmitting: boolean
  breachCount: number
}) {
  const items = [
    'See what your family is learning',
    'Check suspicious links for your family',
    'Get alerts when new threats target your area',
    'Know exactly what to say when they ask',
  ]

  return (
    <>
      <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
        Unsettling, right?
      </h1>
      <p className="text-lg sm:text-xl md:text-2xl text-gray-500 mb-8 max-w-3xl mx-auto">
        {breachCount > 0 ? (
          <>
            AI voices. Stolen data. Real pressure. Today&apos;s scams are sophisticated
            enough to fool anyone. But now you know what you&apos;re up against — and
            that makes all the difference.
          </>
        ) : (
          <>
            AI voices. Slick impersonation. Real pressure. Today&apos;s scams are sophisticated
            enough to fool anyone. But now you know what you&apos;re up against — and
            that makes all the difference.
          </>
        )}
      </p>

      <p className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-12 max-w-3xl mx-auto">
        Here&apos;s how we help you protect your family:
      </p>

      <div className="max-w-2xl mx-auto mb-16 text-left">
        <div className="space-y-5">
          {items.map((item, index) => (
            <motion.div
              key={item}
              className="flex items-start gap-4"
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + index * 0.18, duration: 0.4, ease: 'easeOut' }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.35 + index * 0.18, type: 'spring', stiffness: 300, damping: 20 }}
              >
                <CheckCircle2 className="w-7 h-7 text-emerald-500 flex-shrink-0 mt-0.5" />
              </motion.div>
              <p className="text-lg sm:text-xl md:text-2xl text-gray-600">{item}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.4 }}
      >
        <button
          onClick={onStartTour}
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 bg-violet-600 text-white font-bold px-12 py-5 rounded-full text-xl shadow-2xl hover:bg-violet-700 transition-all cursor-pointer disabled:opacity-50 group hover:scale-[1.02] active:scale-[0.98]"
        >
          {isSubmitting ? 'Loading...' : 'Show me around'}
          <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform ml-1" />
        </button>
      </motion.div>
    </>
  )
}

// ==============================================================================
// INVITED PATH SPECIFIC STEP COMPONENTS
// ==============================================================================

function InvitedWelcomeStep({
  sponsorName,
  onNext,
}: {
  sponsorName: string
  onNext: () => void
}) {
  return (
    <>
      <WelcomeLogo />
      <motion.h1
        className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.12, duration: 0.4, ease: 'easeOut' }}
      >
        Welcome to GranGuard
      </motion.h1>
      <motion.p
        className="text-xl md:text-2xl text-gray-600 mb-4 mx-auto leading-relaxed"
        style={{ maxWidth: '48ch' }}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.24, duration: 0.4, ease: 'easeOut' }}
      >
        {sponsorName} got you access to GranGuard. It's where you'll learn to spot scams, check suspicious links, and stay one step ahead of the bad guys.
      </motion.p>
      <motion.p
        className="text-sm text-gray-500 mb-10"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.36, duration: 0.4, ease: 'easeOut' }}
      >
        Quick setup, then it's all yours.
      </motion.p>
      <motion.button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-lg hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.48, duration: 0.4, ease: 'easeOut' }}
      >
        Let's Go <ChevronRight className="w-5 h-5" />
      </motion.button>
    </>
  )
}

function FamilyConnectionStep({
  sponsorName,
  onNext,
}: {
  sponsorName: string
  onNext: () => void
}) {
  return (
    <>
      <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-[24px] flex items-center justify-center mb-8 mx-auto border border-emerald-200/50">
        <Users className="w-10 h-10 text-emerald-500" />
      </div>
      <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-4">
        Privacy First
      </p>
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
        Connected, but private
      </h1>
      <div className="max-w-md mx-auto mb-8 text-left space-y-4">
        <div className="p-5 bg-emerald-50 rounded-2xl border border-emerald-100">
          <p className="font-bold text-emerald-800 mb-2">What {sponsorName} sees:</p>
          <ul className="text-sm text-emerald-700 space-y-2">
            <li className="flex items-start gap-2">
              <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span>That your protection shield is active and strong</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span>Which protection milestones you've reached</span>
            </li>
          </ul>
        </div>
        <div className="p-5 bg-gray-50 rounded-2xl border border-gray-200">
          <p className="font-bold text-gray-800 mb-2">What {sponsorName} NEVER sees:</p>
          <ul className="text-sm text-gray-600 space-y-2">
            <li className="flex items-start gap-2">
              <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400" />
              <span>Your practice results or quiz answers</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400" />
              <span>Your specific data breach details</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400" />
              <span>Your private questions to our experts</span>
            </li>
          </ul>
        </div>
      </div>
      <p className="text-gray-500 mb-8 text-sm max-w-md mx-auto">
        This is your journey. They're here for peace of mind, not supervision.
      </p>
      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-[#1d1d1f] text-white font-bold px-12 py-5 rounded-full hover:bg-black transition-all cursor-pointer shadow-xl hover:scale-[1.02] active:scale-[0.98]"
      >
        I Understand <ChevronRight className="w-5 h-5 ml-2" />
      </button>
    </>
  )
}

// ==============================================================================
// SHARED LEARNER STEP COMPONENTS
// ==============================================================================

function SelfProtectorWelcomeStep({ onNext, hasWelcomeKit }: { onNext: () => void; hasWelcomeKit: boolean }) {
  return (
    <>
      <WelcomeLogo />
      <motion.h1
        className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.12, duration: 0.4, ease: 'easeOut' }}
      >
        Welcome to GranGuard
      </motion.h1>
      <motion.p
        className="text-xl md:text-2xl text-gray-600 mb-4 mx-auto leading-relaxed"
        style={{ maxWidth: '48ch' }}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.24, duration: 0.4, ease: 'easeOut' }}
      >
        {hasWelcomeKit
          ? "Let's get your account set up first, then we'll collect your shipping address for your Welcome Kit. This takes about 2 minutes."
          : "Let's get your account set up and show you how things work around here. This should only take a couple minutes."
        }
      </motion.p>
      <motion.p
        className="text-sm text-gray-500 mb-10"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.36, duration: 0.4, ease: 'easeOut' }}
      >
        Quick setup, then it's all yours.
      </motion.p>
      {/* <p className="text-base text-gray-500 mb-10">No tests. No jargon. Just practical tools that work.</p> */}
      <motion.button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-bold px-12 py-5 rounded-full text-xl shadow-2xl hover:bg-violet-800 transition-all cursor-pointer disabled:opacity-50 group hover:scale-[1.02] active:scale-[0.98]"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.48, duration: 0.4, ease: 'easeOut' }}
      >
        Let's go <ChevronRight className="w-5 h-5" />
      </motion.button>
    </>
  )
}

// ==============================================================================
// SELF-PROTECTOR ORIENTATION STEPS (new flow)
// ==============================================================================

function SelfNextChoiceStep({
  onLearnMore,
  onBreachScan,
  onSkip,
  isSubmitting,
}: {
  onLearnMore: () => void
  onBreachScan: () => void
  onSkip: () => void
  isSubmitting: boolean
}) {
  const options = [
    {
      id: 'learn_more',
      title: 'Learn how it works',
      desc: 'See how training, tools, and support fit together.',
      icon: <BookOpen className="w-6 h-6" />,
      action: onLearnMore,
      recommended: false,
    },
    {
      id: 'breach_scan',
      title: 'Start a breach scan',
      desc: 'See what data may already be exposed.',
      icon: <Search className="w-6 h-6" />,
      action: onBreachScan,
      recommended: true,
    },
    {
      id: 'dashboard',
      title: 'Go to the dashboard',
      desc: 'Explore everything at your own pace.',
      icon: <LayoutGrid className="w-6 h-6" />,
      action: onSkip,
      recommended: false,
    },
  ]

  return (
    <>
      <p className="text-sm font-bold text-violet-600 uppercase tracking-widest mb-4">
        Your next step
      </p>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 leading-tight">
        Choose what you'd like to do next
      </h1>
      <p className="text-lg text-gray-500 mb-10 max-w-2xl mx-auto">
        Pick one option to keep moving. You can explore everything later.
      </p>
      <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 mb-10">
        {options.map((option) => (
          <button
            key={option.id}
            onClick={option.action}
            disabled={isSubmitting}
            className={`group relative w-full p-6 rounded-3xl border text-left transition-all hover:-translate-y-1 hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed ${
              option.recommended
                ? 'border-violet-600 bg-violet-50'
                : 'border-gray-200 bg-white'
            }`}
          >
            {option.recommended && (
              <span className="absolute top-4 right-4 text-xs font-semibold uppercase tracking-[0.2em] text-violet-600">
                Recommended
              </span>
            )}
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-5 ${
              option.recommended ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-600'
            }`}>
              {option.icon}
            </div>
            <div className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-2">{option.title}</div>
            <div className="text-lg text-gray-500 leading-relaxed">{option.desc}</div>
          </button>
        ))}
      </div>
    </>
  )
}

function SelfUnsettlingStep({ onNext, breachCount }: { onNext: () => void; breachCount: number }) {
  return (
    <>
      <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
        Unsettling, right?
      </h1>
      <p className="text-lg sm:text-xl md:text-2xl text-gray-500 mb-8 max-w-4xl mx-auto">
        {breachCount > 0 ? (
          <>
            AI voices. Stolen data. Real pressure. Today's scams are polished enough to catch anyone off guard.
            But here's the good news: a little training changes everything.
          </>
        ) : (
          <>
            AI voices. Slick impersonation. Real pressure. Today's scams are polished enough to catch anyone off guard.
            But here's the good news: a little training changes everything.
          </>
        )}
      </p>
      
      <p className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-12 max-w-3xl mx-auto">
        That's where we come in.
      </p>
      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-lg hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
      >
        Go on... <ChevronRight className="w-5 h-5" />
      </button>
    </>
  )
}

function SelfWeveGotYourBackStep({
  onStartLesson,
  isSubmitting,
}: {
  onStartLesson: () => void
  isSubmitting: boolean
}) {
  const items = [
    'Strengthen your defenses so fewer scams can reach you',
    'Spot the warning signs that give scams away',
    'Know exactly what to do — and what not to — when targeted',
    'Get a second opinion on anything suspicious',
  ]

  return (
    <>
      <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 mb-4 leading-tight">
        {/* We&apos;ve got your back */}
        We&apos;re going to help you
      </h1>
      <p className="text-lg sm:text-xl md:text-2xl text-gray-500 mb-14 max-w-2xl mx-auto">
        
      </p>

      <div className="max-w-2xl mx-auto mb-16 text-left">
        <div className="space-y-5">
          {items.map((item, index) => (
            <motion.div
              key={item}
              className="flex items-start gap-4"
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + index * 0.18, duration: 0.4, ease: 'easeOut' }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.35 + index * 0.18, type: 'spring', stiffness: 300, damping: 20 }}
              >
                <CheckCircle2 className="w-7 h-7 text-emerald-500 flex-shrink-0 mt-0.5" />
              </motion.div>
              <p className="text-lg sm:text-xl md:text-2xl text-gray-600">{item}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.4 }}
      >
        <button
          onClick={onStartLesson}
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 bg-violet-600 text-white font-bold px-12 py-5 rounded-full text-xl shadow-2xl hover:bg-violet-700 transition-all cursor-pointer disabled:opacity-50 group hover:scale-[1.02] active:scale-[0.98]"
        >
          {isSubmitting ? 'Loading...' : 'Show me how'}
          <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform ml-1" />
        </button>
      </motion.div>
    </>
  )
}

function SelfBreachActivationStep({
  onNext,
  emailAddress,
  isGuardian,
}: {
  onNext: () => void
  emailAddress?: string
  isGuardian?: boolean
}) {
  const [isActive, setIsActive] = useState(false)
  const [isScanning, setIsScanning] = useState(false)
  const [scanFailed, setScanFailed] = useState(false)
  const autoAdvanceRef = useRef(false)
  const advanceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const triggerBreaches = useTriggerBreaches()
  const { data: existingBreaches } = getBreaches()

  const toggleMonitoring = () => {
    if (isScanning) return
    if (isActive) {
      setIsActive(false)
      setIsScanning(false)
      setScanFailed(false)
      autoAdvanceRef.current = false
      if (advanceTimeoutRef.current) {
        clearTimeout(advanceTimeoutRef.current)
        advanceTimeoutRef.current = null
      }
      return
    }
    setIsActive(true)
    setScanFailed(false)
    autoAdvanceRef.current = true
    setIsScanning(true)

    let didFail = false
    const animationDone = new Promise((r) => setTimeout(r, 2200))
    const fetchDone = new Promise<void>((resolve) => {
      triggerBreaches.mutate(undefined, {
        onError: () => { didFail = true },
        onSettled: () => resolve(),
      })
    })
    // Safety timeout: if HIBP or the backend hangs, don't block onboarding forever.
    const safetyTimeout = new Promise<void>((resolve) =>
      setTimeout(() => { didFail = true; resolve() }, 15_000)
    )
    Promise.all([animationDone, Promise.race([fetchDone, safetyTimeout])]).then(() => {
      setIsScanning(false)
      // If the trigger failed but registration already populated breaches,
      // auto-advance silently — the results page will show them fine.
      // If it failed AND there's no data, show a soft error instead of advancing.
      if (didFail && !(existingBreaches && existingBreaches.length > 0)) {
        autoAdvanceRef.current = false
        setScanFailed(true)
      }
    })
  }

  useEffect(() => {
    if (isActive && !isScanning && autoAdvanceRef.current) {
      advanceTimeoutRef.current = setTimeout(() => {
        autoAdvanceRef.current = false
        onNext()
      }, 900)
    }
    return () => {
      if (advanceTimeoutRef.current) {
        clearTimeout(advanceTimeoutRef.current)
        advanceTimeoutRef.current = null
      }
    }
  }, [isActive, isScanning, onNext])

  return (
    <>
      <motion.div
        className="relative mb-10"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <div className="text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            {/* Breach Monitoring */}
            {isGuardian ? "First, let's check your exposure." : "Now, let's check your exposure."}
          </h1>
          <motion.p
            className="text-lg sm:text-xl md:text-2xl text-gray-500 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12, duration: 0.4, ease: 'easeOut' }}
          >

          This tool scans public breach records to see whether any of your information has been exposed in the past. We use it to establish a baseline, monitor for changes, and improve the guidance we give you.

          {/* This tool helps us understand what scammers might already know about you, so we can adjust your training and stay alert for new exposures. */}
                {/* Let's check your past data expsoure to understand what scammers might already know about you.  */}
                {/* Scammers use data breaches to make their messages more convincing. Turn on the Breach Monitor to see what they might already know about you.   */}
                {/* Let's check your exposure and monitor any changes going forward. */}


            {/* Scammers use leaked information to make their messages more convincing. The Breach Monitor looks for any known data exposures linked to you, tailors your training, and keeps watch going forward.  */}


            {/* Scammers use leaked data to target you. Let's see what they already know, and establish a baseline going forward. */}
            {/* Most people have no idea their information is floating around online for scammers to see. Let's find out where you stand. */}

            {/* Breached data is how scams get personal. Let's check your exposure and start monitoring for anything new.
            We'll scan known data breaches for your information and use what we find to personalize your training. */}
          </motion.p>
        </div>
        <div />
      </motion.div>

      <motion.div
        className="max-w-3xl mx-auto"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.24, duration: 0.4, ease: 'easeOut' }}
      >
        <div className="rounded-[32px] border border-gray-200 bg-white/80 p-10 text-left shadow-[0_10px_24px_rgba(15,23,42,0.06)]">
          <div className="flex items-center justify-between">
            <div>
              <div
                className={`inline-flex items-center gap-3 rounded-full px-4 py-2 border mb-4 ${
                  isActive ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'
                }`}
              >
                <span className="relative flex h-3 w-3">
                  {isActive && (
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                  )}
                  <span className={`relative inline-flex h-3 w-3 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                </span>
                <span className={`text-sm font-semibold ${isActive ? 'text-emerald-700' : 'text-gray-500'}`}>
                  {isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <p className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">
                  Data Breach Monitor
                </p>
                <div className="relative group">
                  <button
                    type="button"
                    className="inline-flex h-9 w-9 items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="About the data breach monitor"
                  >
                    <HelpCircle className="h-5 w-5" />
                  </button>
                  <div className="pointer-events-none absolute left-1/2 top-10 z-20 w-[min(320px,calc(100vw-2rem))] -translate-x-1/2 rounded-2xl bg-gray-900 px-4 py-3 text-sm text-white opacity-0 shadow-lg transition-all group-hover:opacity-100">
                    <p className="font-semibold mb-2">What this does</p>
                    <p className="mb-3">
                      Scammers often rely on information from past data breaches to make messages feel convincing. This
                      tool checks whether any of your details have appeared in known breaches, then establishes a safe
                      baseline.
                    </p>
                    <p>
                      We use this only to understand risk and tailor your training and alerts — not to track you or sell
                      your data. Ongoing monitoring helps us spot changes early and guide you if something new appears.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              {!isActive && !isScanning && (
                <div className="pointer-events-none absolute -right-21 -top-10 text-gray-300 scale-x-[-1] scale-y-[-1] rotate-[-35deg]">
                  <svg width="85" height="60" viewBox="0 0 80 60" fill="none">
                    <path
                      d="M5 55 C25 15, 55 10, 75 20"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M66 14 L76 20 L66 26"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      transform="rotate(25 71 35)"
                    />
                  </svg>
                </div>
              )}
              <button
                type="button"
                onClick={toggleMonitoring}
                aria-pressed={isActive}
                className={`relative inline-flex h-14 w-28 items-center rounded-full transition-all ${
                  isActive
                    ? 'bg-emerald-500 hover:bg-emerald-600 shadow-[inset_4px_4px_10px_rgba(16,185,129,0.35),inset_-4px_-4px_10px_rgba(255,255,255,0.25)]'
                    : 'bg-gray-200 hover:bg-gray-300 shadow-[inset_4px_4px_10px_rgba(15,23,42,0.08),inset_-4px_-4px_10px_rgba(255,255,255,0.7)]'
                } ${isScanning ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-[0_6px_16px_rgba(15,23,42,0.12)] transition-all ${
                    isActive ? 'translate-x-14' : 'translate-x-1'
                  }`}
                >
                  <ShieldCheck className={`h-6 w-6 ${isActive ? 'text-emerald-500' : 'text-gray-400'}`} />
                </span>
              </button>
            </div>
          </div>

          <div className="mt-8">
            {isScanning ? (
              <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-6 py-5">
                <div>
                  <p className="text-lg font-semibold text-gray-900">Scanning known breaches…</p>
                  <p className="text-base text-gray-500">This takes just a moment.</p>
                </div>
                <div className="relative h-12 w-12">
                  <div className="absolute inset-0 rounded-full border-2 border-slate-200" />
                  <div className="absolute inset-0 rounded-full border-2 border-slate-500 border-t-transparent animate-spin" />
                </div>
              </div>
            ) : scanFailed ? (
              <div className="rounded-2xl bg-amber-50 border border-amber-100 px-6 py-5">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="h-6 w-6 text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-lg font-semibold text-gray-900">
                      Scan took longer than expected
                    </p>
                    <p className="text-base text-gray-600 mt-1">
                      Your breach monitoring is active and we'll complete the scan in the background. You can check your results anytime from your dashboard.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl bg-slate-50 px-6 py-5">
                <div className="flex items-center justify-between gap-6">
                  <div>
                    <p className="text-lg font-semibold text-gray-900">
                      {isActive ? 'Monitoring enabled' : 'Ready to start your scan'}
                    </p>
                    <p className="text-base text-gray-500">
                      {isActive
                        ? 'We will alert you if new breaches involve this address.'
                        : 'Turn it on to start scanning and monitoring this address.'}
                      {emailAddress && (
                        <span className="block mt-2 text-sm text-gray-400">
                          Monitoring {emailAddress}
                        </span>
                      )}
                    </p>
                  </div>
                  {isActive && (
                    <motion.svg
                      width="48"
                      height="48"
                      viewBox="0 0 48 48"
                      fill="none"
                      initial={{ scale: 0.6, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                    >
                      <motion.circle
                        cx="24"
                        cy="24"
                        r="21"
                        stroke="#10B981"
                        strokeWidth="2"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.4 }}
                      />
                      <motion.path
                        d="M16 25.5L21.5 31L33 18"
                        stroke="#10B981"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.45, delay: 0.1 }}
                      />
                    </motion.svg>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      <button
        onClick={() => {
          if (scanFailed) {
            onNext()
            return
          }
          if (!isActive || isScanning) {
            toggleMonitoring()
            return
          }
          onNext()
        }}
        disabled={isScanning}
        className="mt-10 inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-xl hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {scanFailed ? 'Continue' : 'Activate'}
      </button>
    </>
  )
}

function SelfBreachResultsStep({ onNext }: { onNext: () => void }) {
  const { data: breaches, isLoading: isBreachesLoading } = getBreaches()
  const breachCount = breaches?.length || 0
  // Honesty guard: dataBreachRetrievalDate is only set once a scan has
  // actually completed. If it's null (scan failed or still running, e.g. the
  // fire-and-forget registration scan), we must NOT claim "no breaches
  // found". TD-017.
  const { user: breachClerkUser } = useUser()
  const breachUserQuery = getUserFromClerkId(breachClerkUser?.id || '', { enabled: !!breachClerkUser?.id })
  const scanCompleted = !!breachUserQuery.data?.dataBreachRetrievalDate
  const [hoveredBreach, setHoveredBreach] = useState<number | null>(null)
  const [hoveredType, setHoveredType] = useState<number | null>(null)
  const [autoBreach, setAutoBreach] = useState<number | null>(null)
  const [userInteracted, setUserInteracted] = useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const leftRefs = React.useRef<(HTMLDivElement | null)[]>([])
  const rightRefs = React.useRef<(HTMLDivElement | null)[]>([])
  const [lines, setLines] = useState<
    { x1: number; y1: number; x2: number; y2: number; breachIdx: number; typeIdx: number }[]
  >([])
  const [showGraph, setShowGraph] = useState(false)

  // Map API data classes to friendly labels
  const classMap: Record<string, string> = {
    'Email addresses': 'Email address',
    'Passwords': 'Password',
    'Dates of birth': 'Date of birth',
    'Birth dates': 'Date of birth',
    'Physical addresses': 'Home address',
    'Phone numbers': 'Phone number',
    'Geographic locations': 'Location',
    'IP addresses': 'IP address',
    'Usernames': 'Username',
    'Names': 'Full name',
    'Employers': 'Employer',
    'Job titles': 'Job title',
  }

  // Build breach data with connections to data types
  const breachData = (breaches || [])
    .filter((b) => b.LogoPath && b.Title)
    .map((b) => ({
      name: b.Title,
      logo: b.LogoPath,
      types: Array.from(new Set((b.DataClasses || []).map((dc) => classMap[dc] || dc))),
    }))

  // Unique data types across all breaches
  const displayTypes = Array.from(new Set(breachData.flatMap((b) => b.types))).slice(0, 8)

  // Fallback
  const fallbackTypes = ['Email address', 'Password', 'Date of birth', 'Location']
  const typesToShow = displayTypes.length > 0 ? displayTypes : fallbackTypes

  // Build index mapping: which breach connects to which type indices
  const connections = breachData.map((breach, bIdx) => ({
    breachIdx: bIdx,
    typeIndices: breach.types.map((t) => typesToShow.indexOf(t)).filter((idx) => idx !== -1),
  }))

  // Measure DOM positions and compute SVG lines
  useEffect(() => {
    const measure = () => {
      if (!containerRef.current) return
      const cRect = containerRef.current.getBoundingClientRect()
      const newLines: typeof lines = []

      connections.forEach((conn) => {
        const leftEl = leftRefs.current[conn.breachIdx]
        if (!leftEl) return
        const lRect = leftEl.getBoundingClientRect()

        conn.typeIndices.forEach((tIdx) => {
          const rightEl = rightRefs.current[tIdx]
          if (!rightEl) return
          const rRect = rightEl.getBoundingClientRect()

          newLines.push({
            x1: lRect.right - cRect.left + 4,
            y1: lRect.top + lRect.height / 2 - cRect.top,
            x2: rRect.left - cRect.left - 4,
            y2: rRect.top + rRect.height / 2 - cRect.top,
            breachIdx: conn.breachIdx,
            typeIdx: tIdx,
          })
        })
      })

      setLines(newLines)
    }

    const timer = setTimeout(measure, 50)
    window.addEventListener('resize', measure)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', measure)
    }
  }, [breaches, breachData.length, typesToShow.length])

  // Auto-traverse: cycle through breaches on the left
  useEffect(() => {
    if (userInteracted || breachData.length === 0 || lines.length === 0) return

    // Start after a short delay to let lines render
    const startDelay = setTimeout(() => {
      setAutoBreach(0)
    }, 200)

    return () => clearTimeout(startDelay)
  }, [breachData.length, lines.length, userInteracted])

  useEffect(() => {
    if (isBreachesLoading) {
      setShowGraph(false)
      return
    }
    const raf = requestAnimationFrame(() => setShowGraph(true))
    return () => cancelAnimationFrame(raf)
  }, [isBreachesLoading])

  useEffect(() => {
    if (userInteracted || autoBreach === null || breachData.length === 0) return

    const timer = setTimeout(() => {
      const next = autoBreach + 1
      if (next < breachData.length) {
        setAutoBreach(next)
      } else {
        // Done cycling — clear and stay idle
        setAutoBreach(null)
      }
    }, 1500)

    return () => clearTimeout(timer)
  }, [autoBreach, userInteracted, breachData.length])

  // Stop auto-traverse when user interacts
  const handleUserHover = (setter: (val: number | null) => void, val: number | null) => {
    if (!userInteracted) {
      setUserInteracted(true)
      setAutoBreach(null)
    }
    setter(val)
  }

  // Active breach is either user-hovered or auto-traversed
  const activeBreach = hoveredBreach ?? autoBreach

  // Determine if a line is highlighted
  const isLineHighlighted = (line: (typeof lines)[0]) => {
    if (activeBreach !== null) return line.breachIdx === activeBreach
    if (hoveredType !== null) return line.typeIdx === hoveredType
    return false
  }

  const isAnythingHovered = activeBreach !== null || hoveredType !== null

  // Check if a type is connected to active breach
  const isTypeConnected = (typeIdx: number) => {
    if (activeBreach !== null) {
      return connections[activeBreach]?.typeIndices.includes(typeIdx)
    }
    if (hoveredType !== null) return hoveredType === typeIdx
    return false
  }

  // Check if a breach is connected to hovered type
  const isBreachConnected = (breachIdx: number) => {
    if (activeBreach !== null) return activeBreach === breachIdx
    if (hoveredType !== null) {
      return connections[breachIdx]?.typeIndices.includes(hoveredType)
    }
    return false
  }

  // Scan not (yet) completed: be honest instead of showing the clean bill.
  if (!isBreachesLoading && breachCount === 0 && !scanCompleted) {
    return (
      <>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight">
          Your scan is still running
        </h1>
        <p className="text-xl md:text-2xl text-slate-500 mb-10 max-w-2xl mx-auto leading-relaxed">
          Checking your email against known data breaches can take a little
          while. You don't need to wait: we'll finish in the background and
          your results will be ready on your Breach Monitor.
        </p>
        <button
          onClick={onNext}
          className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-lg hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
        >
          Continue <ChevronRight className="w-5 h-5" />
        </button>
      </>
    )
  }

  // Zero-breach state: completely different slide
  if (!isBreachesLoading && breachCount === 0) {
    return (
      <>
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="mb-8"
        >
          <svg className="w-20 h-20 mx-auto" viewBox="0 0 80 80" fill="none">
            <motion.circle
              cx="40"
              cy="40"
              r="36"
              stroke="#10b981"
              strokeWidth="3"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.2 }}
            />
            <motion.path
              d="M24 42L34 52L56 30"
              stroke="#10b981"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.4, ease: 'easeOut', delay: 0.6 }}
            />
          </svg>
        </motion.div>

        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight">
          Good news. No breaches found.
        </h1>
        <p className="text-xl md:text-2xl text-slate-500 mb-10 max-w-2xl mx-auto leading-relaxed">
          We checked your email against hundreds of known data breaches and it
          came back clean. We'll keep monitoring in the background and alert
          you if that ever changes.
        </p>

        <button
          onClick={onNext}
          className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-lg hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
        >
          Continue <ChevronRight className="w-5 h-5" />
        </button>
      </>
    )
  }

  return (
    <>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
        Here's what scammers can see
      </h1>
      <p className="text-lg sm:text-xl md:text-2xl text-slate-500 mb-10 max-w-3xl mx-auto">
        Your info appeared in{' '}
        <span className="font-semibold text-gray-900">
          {breachCount} data breach{breachCount === 1 ? '' : 'es'}
        </span>
        . Don't worry, this is quite common.
        What matters is knowing that scammers might use this information.
      </p>

      {/* Interactive breach → data type mapping */}
      <div ref={containerRef} className="relative max-w-3xl mx-auto mb-10">
        {isBreachesLoading ? (
          <div className="flex items-start text-left justify-between">
            <div className="w-[46%] sm:w-[240px] min-w-0 sm:flex-shrink-0">
              <div className="h-4 w-40 bg-gray-200 rounded-full mb-4 animate-pulse" />
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="h-8 bg-gray-100 rounded-xl animate-pulse" />
                ))}
              </div>
            </div>
            <div className="w-[46%] sm:w-[240px] min-w-0 sm:flex-shrink-0">
              <div className="h-4 w-32 bg-gray-200 rounded-full mb-4 animate-pulse" />
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="h-8 bg-gray-100 rounded-xl animate-pulse" />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`flex items-start text-left justify-between transition-opacity duration-500 ${
              showGraph ? 'opacity-100' : 'opacity-0'
            }`}
          >
          {/* Left column: breaches */}
          <div className="w-[46%] sm:w-[240px] min-w-0 sm:flex-shrink-0">
            <p className="text-sm font-semibold uppercase tracking-widest text-gray-400 mb-4">
              Who exposed your data
            </p>
            <div className={`${breachData.length > 5 ? 'space-y-0' : 'space-y-2'} ${breachData.length > 8 ? 'max-h-[320px] overflow-y-auto pr-1' : ''}`}>
              {breachData.map((breach, i) => (
                <div
                  key={breach.name}
                  ref={(el) => { leftRefs.current[i] = el }}
                  onMouseEnter={() => handleUserHover(setHoveredBreach, i)}
                  onMouseLeave={() => handleUserHover(setHoveredBreach, null)}
                  className={`flex items-center gap-3 ${breachData.length > 5 ? 'py-1' : 'py-2'} px-3 rounded-xl cursor-default transition-all duration-200 ${
                    isAnythingHovered
                      ? isBreachConnected(i)
                        ? 'opacity-100 bg-violet-50'
                        : 'opacity-30'
                      : 'opacity-100'
                  }`}
                >
                  <img
                    src={breach.logo}
                    alt={breach.name}
                    className="w-6 h-6 rounded object-contain flex-shrink-0"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                  />
                  <span className="text-base font-medium text-gray-900">{breach.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right column: data types */}
          <div className="w-[46%] sm:w-[240px] min-w-0 sm:flex-shrink-0">
            <p className="text-sm font-semibold uppercase tracking-widest text-gray-400 mb-4">
              What was exposed
            </p>
            <div className={typesToShow.length > 5 ? 'space-y-0' : 'space-y-2'}>
              {typesToShow.map((item, i) => (
                <div
                  key={item}
                  ref={(el) => { rightRefs.current[i] = el }}
                  onMouseEnter={() => handleUserHover(setHoveredType, i)}
                  onMouseLeave={() => handleUserHover(setHoveredType, null)}
                  className={`flex items-center gap-3 ${typesToShow.length > 5 ? 'py-1' : 'py-2'} px-3 rounded-xl cursor-default transition-all duration-200 ${
                    isAnythingHovered
                      ? isTypeConnected(i)
                        ? 'opacity-100 bg-amber-50'
                        : 'opacity-30'
                      : 'opacity-100'
                  }`}
                >
                  <div className="w-5 h-5 rounded-full border border-amber-200 bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                  </div>
                  <span className="text-base font-medium text-gray-900">Your {item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        )}

        {/* SVG connection lines */}
        {showGraph && lines.length > 0 && (
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ zIndex: 1 }}
          >
            {lines.map((line, i) => {
              const highlighted = isLineHighlighted(line)
              const midX = (line.x1 + line.x2) / 2
              return (
                <path
                  key={i}
                  d={`M ${line.x1},${line.y1} C ${midX},${line.y1} ${midX},${line.y2} ${line.x2},${line.y2}`}
                  fill="none"
                  stroke={highlighted ? '#7c3aed' : '#d4d4d8'}
                  strokeWidth={highlighted ? 2 : 1}
                  opacity={isAnythingHovered ? (highlighted ? 0.8 : 0.08) : 0.25}
                  className="transition-all duration-200"
                />
              )
            })}
          </svg>
        )}
      </div>

      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-lg hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
      >
        Continue <ChevronRight className="w-5 h-5" />
      </button>
    </>
  )
}

function SelfScamTransitionStep({ onNext, breachCount }: { onNext: () => void; breachCount: number }) {
  return (
    <>
      <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-4 leading-tight">
        What this means for you
      </h1>
      <p className="text-lg sm:text-xl md:text-2xl text-gray-500 mb-12 max-w-2xl mx-auto">
        {breachCount > 0 ? (
          <>
            Scammers may use your personal details to pose as people you trust, including banks, companies, even family members.
          </>
        ) : (
          <>
            A clean scan is a great start, but scammers don't always need stolen data.
            They rely on urgency, impersonation, and emotional pressure to catch
            anyone off guard.
          </>
        )}
      </p>

      <div className="max-w-2xl mx-auto mb-14 text-left bg-gray-50/60 rounded-2xl p-6 flex items-start gap-5 shadow-sm">
        <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center shadow-md">
          <img src={gWhiteLogo} alt="GranGuard" className="w-7 h-7 object-contain" />
        </div>
        <div>
          <p className="text-base font-bold uppercase text-violet-500 mb-0">
            Key Takeaway
          </p>
          <p className="text-2xl font-medium text-gray-900 leading-snug">
            Trust must ALWAYS be verified, never assumed.
          </p>
        </div>
      </div>

      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-xl hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        See an example <ChevronRight className="w-5 h-5" />
      </button>
    </>
  )
}

function SelfScamExampleStep({ onNext, displayName }: { onNext: () => void; displayName: string }) {
  return (
    <>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
        A scam can look this real
      </h1>
      <p className="text-xl text-gray-500 mb-10 max-w-3xl mx-auto">
        These examples are fake, but the tricks are real. Notice the urgency, the personal details, and the
        pressure to click.
      </p>

      <div className="flex justify-center max-w-3xl mx-auto mb-12">
        <div>
          <div className="max-w-lg mx-auto h-[380px] overflow-hidden">
            <div className="bg-gray-900 rounded-[2.5rem] p-3 shadow-2xl overflow-hidden">
              <div className="bg-white rounded-[2rem] overflow-hidden h-[360px]">
                <div className="bg-gray-100 px-6 py-2 flex items-center justify-between text-xs text-gray-500">
                  <span>9:41</span>
                  <div className="flex items-center gap-1">
                    <span>5G</span>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <rect x="1" y="6" width="3" height="12" rx="1" fill="currentColor" />
                      <rect x="6" y="4" width="3" height="14" rx="1" fill="currentColor" />
                      <rect x="11" y="2" width="3" height="16" rx="1" fill="currentColor" />
                      <rect x="16" y="0" width="3" height="18" rx="1" fill="currentColor" opacity="0.3" />
                    </svg>
                  </div>
                </div>

                <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                      <span className="text-gray-600 text-base font-semibold">AMZ</span>
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-gray-900 text-base">Amazon Fraud Dept</p>
                      <p className="text-[10px] text-gray-500">Text Message</p>
                    </div>
                  </div>
                </div>

                <div className="px-3 py-5 bg-white h-full">
                  <p className="text-[10px] text-gray-400 text-center mb-4">Today 9:38 AM</p>
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    className="flex justify-start"
                  >
                    <div className="max-w-[90%] bg-[#34C759] text-white px-4 py-3 rounded-2xl rounded-tl-xs shadow-sm text-left">
                      <p className="text-[18px] leading-tight text-left">
                        <span className="font-bold">Amazon:</span> {displayName}, your order for £500 Amazon Gift Card has been dispatched. If
                        you didn't place this order, visit us at <span className="underline">amazon-frauddept.co/verify</span>
                      </p>
                    </div>
                  </motion.div>
                  <p className="text-[10px] text-gray-400 mt-1.5 ml-1">Delivered</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-lg hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
      >
        Continue <ChevronRight className="w-5 h-5" />
      </button>
    </>
  )
}

function SelfScamVoicemailStep({
  onNext,
  displayName,
  forceRegenerate,
  market,
  preloadedAudio,
}: {
  onNext: () => void
  displayName: string
  forceRegenerate: boolean
  market: string | null
  preloadedAudio: { audioBase64: string; mimeType?: string } | null
}) {
  const { getToken } = useAuth()
  // Static fallback audio (generic, no personalised name)
  const FALLBACK_AUDIO_URL = market === 'GB' || market === 'AU'
    ? '/audio/voicemail-fallback-gb.mp3'
    : '/audio/voicemail-fallback.mp3'

  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoadingAudio, setIsLoadingAudio] = useState(false)
  const [audioError, setAudioError] = useState<string | null>(null)
  const [audioSrc, setAudioSrc] = useState('')
  const [usingFallback, setUsingFallback] = useState(false)
  const [shouldAutoplay, setShouldAutoplay] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [hoverProgress, setHoverProgress] = useState<number | null>(null)
  const [barCount, setBarCount] = useState(0)
  const [hasClickedPlay, setHasClickedPlay] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const waveformRef = useRef<HTMLDivElement | null>(null)

  const buildAudioUrl = (audioBase64: string, mimeType?: string) => {
    const binary = window.atob(audioBase64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i)
    }
    const blob = new Blob([bytes], { type: mimeType || 'audio/mpeg' })
    return URL.createObjectURL(blob)
  }

  const useFallback = () => {
    console.warn('[voicemail] Using fallback audio')
    setAudioSrc(FALLBACK_AUDIO_URL)
    setUsingFallback(true)
    setAudioError(null)
    setIsLoadingAudio(false)
  }

  const generateAudio = async ({ force }: { force?: boolean } = {}) => {
    if (isLoadingAudio || (audioSrc && !force)) return
    setIsLoadingAudio(true)
    setAudioError(null)

    // Timeout — if API doesn't respond in 8s, use fallback
    const timeout = setTimeout(() => {
      console.warn('[voicemail] API timed out, using fallback')
      useFallback()
    }, 8000)

    try {
      const token = await getToken()
      const response = await fetch(`${import.meta.env.VITE_API_URL}/app/voicemail/onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ name: displayName }),
      })
      clearTimeout(timeout)
      if (!response.ok) {
        let serverMsg = ''
        try {
          const errData = await response.json()
          serverMsg = errData?.error || ''
        } catch {}
        throw new Error(serverMsg || 'Failed to generate audio')
      }
      const data = await response.json()
      const url = buildAudioUrl(data.audioBase64, data.mimeType)
      setAudioSrc(url)
    } catch (err: any) {
      clearTimeout(timeout)
      useFallback()
    } finally {
      clearTimeout(timeout)
      setIsLoadingAudio(false)
    }
  }

  const togglePlayback = async () => {
    setHasClickedPlay(true)
    if (!audioRef.current) return
    if (isPlaying) {
      audioRef.current.pause()
      return
    }
    if (forceRegenerate) {
      setAudioSrc('')
      setShouldAutoplay(true)
      await generateAudio({ force: true })
      return
    }
    if (!audioSrc) {
      setShouldAutoplay(true)
      await generateAudio()
      return
    }
    audioRef.current.play()
  }

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    const handleEnded = () => setIsPlaying(false)
    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime || 0)
    const handleLoadedMetadata = () => setDuration(audio.duration || 0)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    return () => {
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
    }
  }, [])

  useEffect(() => {
    if (!shouldAutoplay || !audioSrc || !audioRef.current) return
    audioRef.current.play()
    setShouldAutoplay(false)
  }, [shouldAutoplay, audioSrc])

  // Use preloaded audio if available, otherwise fall back immediately
  useEffect(() => {
    if (audioSrc) return
    if (preloadedAudio?.audioBase64) {
      const url = buildAudioUrl(preloadedAudio.audioBase64, preloadedAudio.mimeType)
      setAudioSrc(url)
    } else {
      // Preload didn't arrive — use fallback so user never waits
      useFallback()
    }
  }, [audioSrc, preloadedAudio])

  useEffect(() => {
    return () => {
      if (audioSrc) {
        URL.revokeObjectURL(audioSrc)
      }
    }
  }, [audioSrc])

  useEffect(() => {
    setCurrentTime(0)
    setDuration(0)
  }, [audioSrc])

  const baseBars = [
    20, 44, 28, 60, 34, 78, 42, 66, 30, 84, 50, 36, 74, 24, 48, 70,
    32, 58, 22, 52, 38, 62, 28, 72, 34, 80, 46, 40, 68, 26, 54, 64,
    24, 56, 36, 76, 44, 82, 32, 66, 26, 60, 40, 78, 48, 64, 22, 58,
  ]
  const bars = useMemo(() => {
    if (barCount <= 0) return baseBars
    return Array.from({ length: barCount }, (_, index) => baseBars[index % baseBars.length])
  }, [barCount, baseBars])
  const progress = duration > 0 ? Math.min(currentTime / duration, 1) : 0
  const activeProgress = hoverProgress ?? progress
  const formatTimestamp = (value: number) => {
    const total = Math.max(0, Math.floor(value))
    const minutes = Math.floor(total / 60)
    const seconds = total % 60
    return `${minutes}:${String(seconds).padStart(2, '0')}`
  }

  const updateHoverFromEvent = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = waveformRef.current?.getBoundingClientRect()
    if (!rect) return
    const nextProgress = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1)
    setHoverProgress(nextProgress)
  }

  const seekToHover = () => {
    if (hoverProgress === null || !audioRef.current || duration <= 0) return
    audioRef.current.currentTime = hoverProgress * duration
    setCurrentTime(audioRef.current.currentTime)
  }

  useEffect(() => {
    const element = waveformRef.current
    if (!element) return

    const BAR_WIDTH = 6
    const BAR_GAP = 4

    const updateBarCount = () => {
      const width = element.getBoundingClientRect().width
      if (!width) return
      const count = Math.max(1, Math.floor((width + BAR_GAP) / (BAR_WIDTH + BAR_GAP)))
      setBarCount(count)
    }

    updateBarCount()

    const observer = new ResizeObserver(() => updateBarCount())
    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  return (
    <>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
      Imagine receiving this message
      </h1>
      <p className="text-xl text-gray-500 mb-10 max-w-3xl mx-auto">
      You're at home when your phone rings. It sounds official, urgent, and mentions your name and account. Take a moment to notice how it makes you feel.
      </p>

      <div className="max-w-4xl mx-auto mb-12">
        <div className="bg-white border border-gray-200 rounded-3xl shadow-xl p-8">
          <div className="flex items-center justify-between gap-6 mb-6">
            <div className="flex items-center gap-4">
              {/* <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-500 text-white flex items-center justify-center shadow-md">
                <Phone className="w-6 h-6" />
              </div> */}
              <div>
                <p className="text-sm text-gray-500 text-left">Simulated Voicemail</p>
                <p className="text-lg font-semibold text-gray-900">
                  {market === 'GB' || market === 'AU' ? 'Metropolitan Police' : 'Police Department'}
                </p>
              </div>
            </div>
            <span className="text-xs text-right flex-1">
              {audioError ? (
                <span className="text-amber-600">
                  {audioError}{' '}
                  <button type="button" onClick={() => generateAudio({ force: true })} className="underline cursor-pointer hover:text-amber-700">Retry</button>
                </span>
              ) : (
                <span className="text-gray-400">Tap play to listen</span>
              )}
            </span>
          </div>

          <div className="flex items-center gap-6">
            <div className="relative">
              {!hasClickedPlay && (
                <div className="pointer-events-none absolute -left-21 -top-10 text-gray-300 rotate-[30deg] scale-y-[-1]">
                  <svg width="85" height="60" viewBox="0 0 80 60" fill="none">
                    <path
                      d="M5 55 C25 15, 55 10, 75 20"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M66 14 L76 20 L66 26"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      transform="rotate(25 71 35)"
                    />
                  </svg>
                </div>
              )}
              <button
                type="button"
                onClick={togglePlayback}
                disabled={isLoadingAudio}
                className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-6 py-3 rounded-full shadow-lg shadow-violet-200 hover:bg-violet-700 hover:shadow-xl hover:shadow-violet-300 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingAudio ? 'Preparing' : isPlaying ? 'Pause' : 'Play'}
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
            </div>

            <div className="flex flex-1 flex-col items-start">
              <div
                ref={waveformRef}
                onMouseMove={updateHoverFromEvent}
                onMouseLeave={() => setHoverProgress(null)}
                onClick={seekToHover}
                className="relative flex items-center gap-[4px] h-24 cursor-pointer w-full"
              >
                {bars.map((height, index) => {
                  const played = activeProgress >= (index + 1) / bars.length
                  return (
                    <div
                      key={index}
                      className={`w-[6px] rounded-full transition-colors ${
                        played ? 'bg-violet-500' : 'bg-gray-200'
                      }`}
                      style={{ height }}
                    />
                  )
                })}
                {hoverProgress !== null && (
                  <div
                    className="absolute top-0 bottom-0 w-px bg-violet-400"
                    style={{ left: `${hoverProgress * 100}%` }}
                  />
                )}
              </div>
              <div className="relative mt-2 h-4 w-full text-xs text-gray-400">
                <span className="absolute left-0">{formatTimestamp(currentTime)}</span>
                <span className="absolute right-0">{formatTimestamp(duration)}</span>
              </div>
            </div>
          </div>

          <div className="mt-7 border-t border-gray-100 pt-5 text-left">
            <p className="text-sm font-semibold text-gray-600 mb-1">
              Transcript
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              {market === 'GB' || market === 'AU'
                ? usingFallback
                  ? '"Hello, this is Sergeant Williams calling from the Metropolitan Police. I\'m afraid a family member of yours has been involved in a serious road traffic accident. They\'re currently being held and we need to arrange a payment for their release. Could you ring me back on this number as soon as possible please."'
                  : `"Hello, is that ${displayName}? This is Sergeant Williams calling from the Metropolitan Police. I'm afraid a family member of yours has been involved in a serious road traffic accident. They're currently being held and we need to arrange a payment for their release. Could you ring me back on this number as soon as possible please."`
                : usingFallback
                  ? '"Hi, this is Sergeant Williams from the Metropolitan Police. I\'m calling about a family member of yours that\'s been involved in a serious traffic accident. We just need a payment for their legal fees to process their release. Please call me back as soon as possible."'
                  : `"Hi ${displayName}, this is Sergeant Williams from the police department. I'm calling about a family member of yours that's been involved in a serious traffic accident. We just need a payment for their legal fees to process their release. Please call me back as soon as possible."`
              }
            </p>
          </div>
        </div>
      </div>

      <audio ref={audioRef} src={audioSrc || undefined} preload="auto" />

      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-lg hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
      >
        Continue <ChevronRight className="w-5 h-5" />
      </button>
    </>
  )
}

function SelfScamSimulationStep({ onNext }: { onNext: () => void }) {
  const [selectedFlags, setSelectedFlags] = useState<string[]>([])
  const [viewMode, setViewMode] = useState<'sms' | 'email'>('sms')

  const toggleFlag = (id: string) => {
    setSelectedFlags((prev) =>
      prev.includes(id) ? prev.filter((flag) => flag !== id) : [...prev, id]
    )
  }

  const isHighlighted = (id: string) => selectedFlags.includes(id)

  const flags = [
    { id: 'sender', label: 'Unknown number' },
    { id: 'urgency', label: 'Creates urgency' },
    { id: 'link', label: 'Suspicious link' },
    { id: 'personal', label: 'Uses your info' },
  ]

  const emailFlags = [
    { id: 'sender', label: 'Look-alike sender' },
    { id: 'urgency', label: 'Urgent language' },
    { id: 'link', label: 'Suspicious link' },
    { id: 'personal', label: 'Uses your info' },
  ]

  return (
    <>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
        Spot the red flags
      </h1>
      <p className="text-xl text-gray-500 mb-8 max-w-xl mx-auto">
        This is a simulation. Tap the warning signs you notice.
      </p>

      <div className="flex items-center justify-center gap-3 mb-8">
        <button
          type="button"
          onClick={() => setViewMode('sms')}
          className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${
            viewMode === 'sms'
              ? 'bg-violet-600 text-white shadow-sm'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          Text message
        </button>
        <button
          type="button"
          onClick={() => setViewMode('email')}
          className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${
            viewMode === 'email'
              ? 'bg-violet-600 text-white shadow-sm'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          Email
        </button>
      </div>

      {viewMode === 'sms' ? (
        <>
          {/* iPhone-style SMS container (top half only) */}
          <div className="max-w-sm mx-auto mb-8">
            <div className="bg-gray-900 rounded-[2.5rem] p-3 shadow-2xl overflow-hidden">
              <div className="bg-white rounded-[2rem] overflow-hidden h-[360px]">
                {/* Status bar */}
                <div className="bg-gray-100 px-6 py-2 flex items-center justify-between text-xs text-gray-500">
                  <span>9:41</span>
                  <div className="flex items-center gap-1">
                    <span>5G</span>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <rect x="1" y="6" width="3" height="12" rx="1" fill="currentColor" />
                      <rect x="6" y="4" width="3" height="14" rx="1" fill="currentColor" />
                      <rect x="11" y="2" width="3" height="16" rx="1" fill="currentColor" />
                      <rect x="16" y="0" width="3" height="18" rx="1" fill="currentColor" opacity="0.3" />
                    </svg>
                  </div>
                </div>

                {/* Message header */}
                <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center justify-center gap-2">
                    <div
                      className={`w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center transition-all ${
                        isHighlighted('sender') ? 'ring-2 ring-amber-400 ring-offset-2' : ''
                      }`}
                    >
                      <span className="text-gray-500 text-lg">#</span>
                    </div>
                    <div className="text-center">
                      <p
                        className={`font-semibold text-gray-900 text-sm transition-all ${
                          isHighlighted('sender') ? 'bg-amber-100 px-2 py-0.5 rounded' : ''
                        }`}
                      >
                        +1 (833) 924-8847
                      </p>
                      <p className="text-[10px] text-gray-500">Text Message</p>
                    </div>
                  </div>
                </div>

                {/* Messages area */}
                <div className="px-3 py-5 bg-white">
                  <p className="text-[10px] text-gray-400 text-center mb-4">Today 9:38 AM</p>
                  <div className="flex justify-start">
                    <div className="max-w-[90%] bg-[#34C759] text-white px-4 py-3 rounded-2xl rounded-tl-sm shadow-sm">
                      <p className="text-[15px] leading-relaxed">
                        <span
                          className={`font-semibold ${
                            isHighlighted('urgency') ? 'bg-white/30 px-1 rounded' : ''
                          } transition-all`}
                        >
                          Fraud Alert:
                        </span>{' '}
                        <span
                          className={`${
                            isHighlighted('personal') ? 'bg-white/30 px-1 rounded' : ''
                          } transition-all`}
                        >
                          Margaret
                        </span>
                        , we blocked a charge on your account ending in{' '}
                        <span
                          className={`font-semibold ${
                            isHighlighted('personal') ? 'bg-white/30 px-1 rounded' : ''
                          } transition-all`}
                        >
                          4821
                        </span>
                        .{' '}
                        <span
                          className={`${
                            isHighlighted('urgency') ? 'bg-white/30 px-1 rounded' : ''
                          } transition-all`}
                        >
                          Confirm now
                        </span>{' '}
                        to avoid a hold:{' '}
                        <span
                          className={`underline ${
                            isHighlighted('link') ? 'bg-white/30 px-1 rounded' : ''
                          } transition-all`}
                        >
                          secure-bankverify.com/confirm
                        </span>
                      </p>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1.5 ml-1">Delivered</p>
                </div>
              </div>
            </div>
            <div className="text-center mt-4">
              <span className="text-xs uppercase tracking-[0.2em] text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                Simulation — safe to review
              </span>
            </div>
          </div>

          <div className="max-w-xl mx-auto mb-10">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-400 mb-4">
              Tap the red flags
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              {flags.map((flag) => (
                <button
                  key={flag.id}
                  type="button"
                  onClick={() => toggleFlag(flag.id)}
                  className={`rounded-full px-5 py-2.5 text-base font-semibold transition-all cursor-pointer ${
                    selectedFlags.includes(flag.id)
                      ? 'bg-amber-100 text-amber-800 shadow-sm'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {flag.label}
                </button>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="max-w-2xl mx-auto mb-8 bg-white/90 border border-gray-200 rounded-3xl shadow-sm text-left overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-gray-400">Email</p>
                  <p
                    className={`text-lg font-semibold text-gray-900 ${
                      isHighlighted('urgency') ? 'bg-amber-100 inline-block px-1 rounded' : ''
                    }`}
                  >
                    Immediate action required
                  </p>
                </div>
                <span className="text-xs text-gray-400">Simulation</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                From:{' '}
                <span
                  className={`font-semibold text-gray-700 ${
                    isHighlighted('sender') ? 'bg-amber-100 px-1 rounded' : ''
                  }`}
                >
                  Security Team &lt;support@granguard-security.com&gt;
                </span>
              </p>
            </div>
            <div className="px-6 py-6 space-y-4 text-gray-700">
              <p>
                Hi{' '}
                <span className={`${isHighlighted('personal') ? 'bg-amber-100 px-1 rounded' : ''}`}>
                  Margaret
                </span>
                ,
              </p>
              <p>
                We detected a sign-in attempt for your account ending in{' '}
                <span className={`${isHighlighted('personal') ? 'bg-amber-100 px-1 rounded' : ''}`}>
                  4821
                </span>{' '}
                from a new device. For your protection, we temporarily limited access.
              </p>
              <p>
                <span className={`${isHighlighted('urgency') ? 'bg-amber-100 px-1 rounded' : ''}`}>
                  Please verify your details within 30 minutes
                </span>{' '}
                to restore access.
              </p>
              <div>
                <button
                  type="button"
                  className={`inline-flex items-center rounded-full px-5 py-2 text-sm font-semibold ${
                    isHighlighted('link') ? 'bg-amber-100 text-amber-800' : 'bg-violet-600 text-white'
                  }`}
                >
                  Verify your account
                </button>
              </div>
              <p className="text-sm text-gray-500">
                Or copy this link into your browser:{' '}
                <span className={`${isHighlighted('link') ? 'bg-amber-100 px-1 rounded' : ''}`}>
                  https://granguard-security.com/verify
                </span>
              </p>
            </div>
          </div>

          <div className="max-w-xl mx-auto mb-10">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-400 mb-4">
              Tap the red flags
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              {emailFlags.map((flag) => (
                <button
                  key={flag.id}
                  type="button"
                  onClick={() => toggleFlag(flag.id)}
                  className={`rounded-full px-5 py-2.5 text-base font-semibold transition-all cursor-pointer ${
                    selectedFlags.includes(flag.id)
                      ? 'bg-amber-100 text-amber-800 shadow-sm'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {flag.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Red flags */}
      <div className="max-w-xl mx-auto mb-10">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-400 mb-4">
          Tap the red flags
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          {flags.map((flag) => (
            <button
              key={flag.id}
              type="button"
              onClick={() => toggleFlag(flag.id)}
              className={`rounded-full px-5 py-2.5 text-base font-semibold transition-all cursor-pointer ${
                selectedFlags.includes(flag.id)
                  ? 'bg-amber-100 text-amber-800 shadow-sm'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {flag.label}
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={onNext}
        disabled={selectedFlags.length === 0}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-xl hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Continue <ChevronRight className="w-5 h-5" />
      </button>
    </>
  )
}

function SelfYourDefenseStep({ onNext }: { onNext: () => void }) {
  const layers = [
    {
      icon: <ShieldCheck className="w-8 h-8" strokeWidth={1.2} />,
      iconClass: 'text-violet-600',
      iconBg: 'bg-violet-50',
      cardBg: 'bg-violet-50/40',
      name: 'Protection',
      desc: 'Reduce your risk and exposure',
      detail: 'Strengthen your daily habits and security settings so fewer scams can reach you.',
    },
    {
      icon: <Search className="w-8 h-8" strokeWidth={1.2} />,
      iconClass: 'text-amber-600',
      iconBg: 'bg-amber-50',
      cardBg: 'bg-amber-50/40',
      name: 'Detection',
      desc: 'Spot manipulation tactics',
      detail: 'Learn the signals that something is off before you click, reply, or pay.',
    },
    {
      icon: <LifeBuoy className="w-8 h-8" strokeWidth={1.2} />,
      iconClass: 'text-emerald-600',
      iconBg: 'bg-emerald-50',
      cardBg: 'bg-emerald-50/40',
      name: 'Response',
      desc: 'Know what to do when targeted',
      detail: 'Get clear next steps for stopping damage and getting help fast.',
    },
    {
      icon: <Users className="w-8 h-8" strokeWidth={1.2} />,
      iconClass: 'text-sky-600',
      iconBg: 'bg-sky-50',
      cardBg: 'bg-sky-50/40',
      name: 'Connection',
      desc: 'Build a support network of allies',
      detail: 'Loop in trusted people so you are never handling scams alone.',
    },
  ]

  return (
    <>
      {/* <p className="text-base font-bold text-violet-600 uppercase tracking-widest mb-4">
        Our approach
      </p> */}
      <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
      How This Works
      </h1>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.3 }}
        className="text-lg sm:text-xl md:text-2xl text-gray-500 mb-10 max-w-3xl mx-auto"
      >
        {/* These layers form the core of your scam defense.  */}
        {/* We're here to help you build these core layers of scam defense. */}
        {/* Everything in GranGuard is built around these four pillars of scam defense. */}
        We're going to help you build strength in four areas. 

      </motion.p>
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row items-stretch gap-3 mb-8">
          {layers.map((layer, i) => (
            <React.Fragment key={layer.name}>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 + 0.12 * i, duration: 0.35, ease: 'easeOut' }}
                className={`group relative flex-1 p-5 sm:p-6 text-left ${layer.cardBg} rounded-2xl border border-white/60 shadow-[0_10px_24px_rgba(15,23,42,0.06)] transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_18px_36px_rgba(15,23,42,0.1)] cursor-pointer z-0`}
              >
                <div className="pointer-events-none absolute -top-14 left-1/2 z-20 w-[min(300px,calc(100vw-2rem))] -translate-x-1/2 opacity-0 transition-all duration-200 group-hover:-top-20 group-hover:opacity-100">
                  <div className="rounded-2xl bg-gray-900 text-white text-base px-5 py-4 shadow-lg">
                    {layer.detail}
                  </div>
                </div>
                <div className={`${layer.iconBg} ${layer.iconClass} mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl`}>{layer.icon}</div>
                <div className="font-semibold text-2xl sm:text-3xl tracking-normal text-gray-900 mb-2">
                 {layer.name}
                </div>
                <div className="text-lg sm:text-xl text-gray-500 leading-relaxed sm:max-w-[180px]">{layer.desc}</div>
              </motion.div>
              {i < layers.length - 1 && (
                <div className="flex items-center justify-center text-slate-400/80 rotate-90 sm:rotate-0">
                  <ChevronRight className="w-5 h-5" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      <button
        onClick={onNext}
        className="mt-8 inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-xl hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
      >
        Continue <ChevronRight className="w-5 h-5" />
      </button>
    </>
  )
}

function SelfWhatToExpectStep({ onNext }: { onNext: () => void }) {
  const fromUs = [
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: 'Guided skill-building',
      desc: 'Short lessons that adapt to what matters most to you.',
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: 'Threat insights',
      desc: 'Real-world scam intel so you stay ahead of what is new.',
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Continuous support',
      desc: 'We have your back with guidance and tools at every step.',
    },
  ]
  const fromYou = [
    {
      icon: <Target className="w-6 h-6" />,
      title: 'A few minutes a week',
      desc: 'Short lessons and tips to build real confidence.',
    },
    {
      icon: <CheckCircle2 className="w-6 h-6" />,
      title: 'Pause before you act',
      desc: 'Use the scanner or tips when something feels off.',
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Loop in your people',
      desc: 'Bring a trusted person in when you need a second opinion.',
    },
  ]

  return (
    <>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.3 }}
        className="text-xl text-gray-500 mb-12 max-w-3xl mx-auto"
      >
        {/* A clear promise from us — and a simple promise from you. */}
      </motion.p>
      <div className="max-w-5xl mx-auto grid grid-cols-2 gap-12 mb-12 text-left">
        <div>
          <p className="text-3xl font-bold text-gray-900 mb-6">
            What you can expect from us
          </p>
          <div>
            {fromUs.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + 0.12 * i, duration: 0.35, ease: 'easeOut' }}
                className="flex items-start gap-6 py-6"
              >
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600">
                    {item.icon}
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-lg sm:text-xl md:text-2xl text-gray-900 mb-1">{item.title}</p>
                  <p className="text-xl text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-3xl font-bold text-gray-900 mb-6">
            What we expect from you
          </p>
          <div>
            {fromYou.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 + 0.12 * i, duration: 0.35, ease: 'easeOut' }}
                className="flex items-start gap-6 py-6"
              >
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600">
                    {item.icon}
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-lg sm:text-xl md:text-2xl text-gray-900 mb-1">{item.title}</p>
                  <p className="text-xl text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-10 py-4 rounded-full text-xl hover:bg-violet-700 transition-all cursor-pointer shadow-lg shadow-violet-200"
      >
        Continue <ChevronRight className="w-5 h-5" />
      </button>
    </>
  )
}

function SelfChooseStartStep({
  onComplete,
  isSubmitting,
}: {
  onComplete: (destination: string) => Promise<void>
  isSubmitting: boolean
}) {
  const options = [
    {
      id: 'approach',
      destination: '/learn',
      icon: <Info className="w-6 h-6" />,
      title: 'Learn more about our approach',
      desc: 'Understand how GranGuard works and what to expect before diving in.',
      recommended: true,
    },
    {
      id: 'exercise',
      destination: '/learn',
      icon: <Play className="w-6 h-6" />,
      title: 'Get started on your first exercise',
      desc: 'Jump straight into a short, practical activity to build your first skill.',
      recommended: false,
    },
    {
      id: 'dashboard',
      destination: '/',
      icon: <LayoutDashboard className="w-6 h-6" />,
      title: 'Skip to the dashboard',
      desc: 'Explore on your own — you can always come back to guided content later.',
      recommended: false,
    },
  ]

  return (
    <>
      <p className="text-sm font-bold text-violet-600 uppercase tracking-widest mb-4">
        You're all set
      </p>
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 leading-tight">
        What would you like<br />to do next?
      </h1>
      <p className="text-base text-gray-500 mb-8 max-w-md mx-auto">
        There's no wrong answer — pick what feels right for you.
      </p>
      <div className="w-full max-w-lg mx-auto space-y-4">
        {options.map((option) => (
          <button
            key={option.id}
            disabled={isSubmitting}
            onClick={() => onComplete(option.destination)}
            className={`w-full p-6 rounded-3xl border-2 text-left flex justify-between items-start cursor-pointer transition-all hover:-translate-y-1 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed ${
              option.recommended
                ? 'border-violet-600 bg-violet-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                option.recommended
                  ? 'bg-violet-600 text-white'
                  : 'bg-gray-100 text-gray-500'
              }`}>
                {option.icon}
              </div>
              <div>
                <div className={`font-bold mb-1 ${
                  option.recommended ? 'text-violet-900' : 'text-gray-900'
                }`}>
                  {option.title}
                </div>
                <div className={`text-sm ${
                  option.recommended ? 'text-violet-700/70' : 'text-gray-500'
                }`}>
                  {option.desc}
                </div>
              </div>
            </div>
            {option.recommended && (
              <div className="bg-violet-600 text-white text-xs font-bold px-3 py-1 rounded-full flex-shrink-0">
                RECOMMENDED
              </div>
            )}
          </button>
        ))}
      </div>
    </>
  )
}

function NameCaptureStep({
  name,
  setName,
  onNext,
}: {
  name: string
  setName: (name: string) => void
  onNext: () => void
}) {
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) onNext(); }}>
      <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight">
        {/* What should we call you? */}
        Let's start with your name.
      </h1>
      <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
        {/* First name, nickname, or superhero alias - whatever you prefer. */}
        We'll use this to personalise the app for you.
      </p>
      <div className="max-w-sm mx-auto mb-10">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Margaret, Larry, Big Papa"
          className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl text-2xl focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 text-center"
          autoFocus
        />
      </div>
      <button
        type="submit"
        disabled={!name.trim()}
        className={`inline-flex items-center gap-2 font-semibold px-10 py-4 rounded-full text-lg transition-all ${!name.trim() ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-violet-600 text-white hover:bg-violet-700 cursor-pointer shadow-lg shadow-violet-200'}`}
      >
        Continue <ChevronRight className="w-5 h-5" />
      </button>
    </form>
  )
}

function MindsetQuestionStep({
  selectedAnswer,
  setSelectedAnswer,
  onNext,
}: {
  selectedAnswer: string | null
  setSelectedAnswer: (answer: string) => void
  onNext: () => void
}) {
  const options = [
    { id: 'a', text: "People who aren't tech-savvy" },
    { id: 'b', text: 'People who are too trusting' },
    { id: 'c', text: "People who don't pay attention" },
    { id: 'd', text: 'Anyone, regardless of intelligence' },
  ]

  return (
    <>
      <p className="text-[13px] font-bold text-violet-600 uppercase tracking-[0.2em] mb-6">
        Quick Question
      </p>
      <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-10 leading-[1.1] tracking-tight">
        Who do you think<br />falls for scams?
      </h1>
      <div className="space-y-4 max-w-xl mx-auto">
        {options.map((opt) => (
          <button
            key={opt.id}
            onClick={() => setSelectedAnswer(opt.id)}
            className={`w-full p-6 rounded-3xl border-2 text-left text-lg font-bold transition-all cursor-pointer ${
              selectedAnswer === opt.id
                ? 'border-violet-600 bg-violet-50 text-violet-700 shadow-md'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            {opt.text}
          </button>
        ))}
      </div>
      <button
        onClick={onNext}
        disabled={!selectedAnswer}
        className="inline-flex items-center gap-2 bg-[#1d1d1f] text-white font-bold px-12 py-5 rounded-full mt-12 hover:bg-black transition-all cursor-pointer shadow-xl hover:scale-[1.02] active:scale-[0.98] disabled:opacity-30 disabled:grayscale disabled:scale-100 disabled:cursor-not-allowed"
      >
        Continue <ChevronRight className="w-5 h-5 ml-2" />
      </button>
    </>
  )
}

function MindsetRevealStep({
  isInvited,
  sponsorName,
  onNext,
}: {
  isInvited: boolean
  sponsorName: string
  onNext: () => void
}) {
  return (
    <>
      <p className="text-xs font-bold text-violet-600 uppercase tracking-widest mb-6">
        The Truth About Scams
      </p>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
        It's not about being<br />
        <span className="text-violet-600">"tech-savvy"</span>
      </h1>
      <p className="text-xl text-gray-600 mb-6 max-w-lg mx-auto leading-relaxed">
        Scammers don't target your intelligence—they target your emotions. Even experts get caught when they're rushed, worried, or excited.
      </p>
      {isInvited ? (
        <p className="text-gray-500 mb-6 max-w-md mx-auto">
          That's why {sponsorName} invited you. It's about seeing the trap before it snaps, so you can stay in control.
        </p>
      ) : (
        <p className="text-gray-500 mb-6 max-w-md mx-auto">
          You've got the life experience. We're just giving you the digital version of your existing "street smarts."
        </p>
      )}
      <div className="p-8 bg-gradient-to-br from-violet-50 to-indigo-50 rounded-3xl text-center max-w-sm mx-auto mb-8 border border-violet-100">
        <p className="text-lg font-bold text-violet-600 mb-2">The Good News</p>
        <p className="text-gray-700">
          Once you see the patterns, they become obvious. Let's look at the triggers they use.
        </p>
      </div>
      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-[#1d1d1f] text-white font-bold px-12 py-5 rounded-full hover:bg-black transition-all cursor-pointer shadow-xl hover:scale-[1.02] active:scale-[0.98]"
      >
        Show Me <ChevronRight className="w-5 h-5 ml-2" />
      </button>
    </>
  )
}

function TriggersStep({ onNext }: { onNext: () => void }) {
  const triggers = [
    {
      icon: <ShieldAlert className="w-6 h-6 text-rose-500" />,
      title: 'Fear',
      desc: '"Your account is at risk"',
    },
    {
      icon: <Zap className="w-6 h-6 text-amber-500" />,
      title: 'Urgency',
      desc: '"Act now or lose this"',
    },
    {
      icon: <Lock className="w-6 h-6 text-blue-500" />,
      title: 'Authority',
      desc: '"I\'m calling from your bank"',
    },
    {
      icon: <Trophy className="w-6 h-6 text-emerald-500" />,
      title: 'Reward',
      desc: '"You\'ve won a prize"',
    },
  ]

  return (
    <>
      <p className="text-xs font-bold text-violet-600 uppercase tracking-widest mb-6">
        The Four Triggers
      </p>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
        Scammers exploit<br />these emotions
      </h1>
      <p className="text-xl text-gray-600 mb-8 max-w-lg mx-auto">
        Every scam uses at least one of these psychological triggers to bypass your rational thinking.
      </p>
      <div className="grid grid-cols-2 gap-4 max-w-md mx-auto mb-10">
        {triggers.map((trigger) => (
          <div
            key={trigger.title}
            className="p-6 bg-gray-50 rounded-3xl text-center border border-gray-100"
          >
            <div className="mb-3 flex justify-center">{trigger.icon}</div>
            <div className="font-bold text-gray-900 mb-1">{trigger.title}</div>
            <div className="text-xs text-gray-500 italic">{trigger.desc}</div>
          </div>
        ))}
      </div>
      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-[#1d1d1f] text-white font-bold px-12 py-5 rounded-full hover:bg-black transition-all cursor-pointer shadow-xl hover:scale-[1.02] active:scale-[0.98]"
      >
        Continue <ChevronRight className="w-5 h-5 ml-2" />
      </button>
    </>
  )
}

function PersonalIntroStep({ onNext }: { onNext: () => void }) {
  const items = [
    'Your name and address',
    'Your email and phone number',
    'Where you bank',
    'Names of family members',
    "Recent purchases you've made",
  ]

  return (
    <>
      <p className="text-xs font-bold text-amber-600 uppercase tracking-widest mb-6">
        Why It Feels Real
      </p>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
        Here's something<br />uncomfortable
      </h1>
      <div className="bg-white rounded-3xl border border-gray-200 p-8 max-w-md mx-auto text-left mb-8 shadow-sm">
        <p className="text-lg text-gray-900 mb-6 leading-relaxed">
          Scammers may already know:
        </p>
        <ul className="space-y-3 text-gray-600">
          {items.map((item) => (
            <li key={item} className="flex items-center gap-3">
              <span className="w-2 h-2 bg-amber-500 rounded-full flex-shrink-0"></span>
              {item}
            </li>
          ))}
        </ul>
        <p className="text-sm text-gray-500 mt-6">
          This comes from data breaches - when companies get hacked and customer data gets stolen and sold.
        </p>
      </div>
      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-[#1d1d1f] text-white font-bold px-12 py-5 rounded-full hover:bg-black transition-all cursor-pointer shadow-xl hover:scale-[1.02] active:scale-[0.98]"
      >
        How Does This Affect Me? <ChevronRight className="w-5 h-5 ml-2" />
      </button>
    </>
  )
}

function PersonalExampleStep({
  name,
  onNext,
}: {
  name: string
  onNext: () => void
}) {
  return (
    <>
      <p className="text-xs font-bold text-amber-600 uppercase tracking-widest mb-6">
        See The Difference
      </p>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight">
        This is why scams<br />feel convincing
      </h1>
      <div className="grid gap-4 max-w-lg w-full mx-auto mb-8">
        <div className="bg-white rounded-2xl border border-gray-200 p-6 text-left opacity-60">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
            Generic Scam
          </p>
          <p className="text-gray-500 italic">
            "Dear Customer, your account has a problem. Click here to verify."
          </p>
        </div>
        <div className="bg-violet-50 rounded-2xl border-2 border-violet-300 p-6 text-left">
          <p className="text-xs font-bold text-violet-600 uppercase tracking-widest mb-2">
            Targeted Scam
          </p>
          <p className="text-gray-900">
            "Hi {name || 'Margaret'}, we noticed unusual activity on your Barclays account ending in 4521. Please verify your identity immediately."
          </p>
        </div>
      </div>
      <p className="text-gray-600 mb-8 max-w-md text-center">
        They <strong className="text-gray-900">know</strong> things about you. That's why it feels real.
      </p>
      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-[#1d1d1f] text-white font-bold px-12 py-5 rounded-full hover:bg-black transition-all cursor-pointer shadow-xl hover:scale-[1.02] active:scale-[0.98]"
      >
        How Do I Protect Myself? <ChevronRight className="w-5 h-5 ml-2" />
      </button>
    </>
  )
}

function PersonalReframeStep({ onNext }: { onNext: () => void }) {
  return (
    <>
      <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-6">
        Key Insight
      </p>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
        Now you know<br />something important
      </h1>
      <div className="p-8 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl text-center max-w-md mx-auto mb-8 border border-emerald-100">
        <p className="text-xl font-bold text-emerald-700 mb-3">
          Personal details ≠ Legitimate contact
        </p>
        <p className="text-gray-700 leading-relaxed">
          Just because someone knows your name, your bank, or your address doesn't mean they <em>are</em> your bank.
        </p>
      </div>
      <p className="text-lg text-gray-500 mb-8">
        This single insight stops most scams.
      </p>
      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-8 py-4 rounded-full hover:bg-violet-700 transition-all cursor-pointer"
      >
        Got It
      </button>
    </>
  )
}

function ExerciseIntroStep({
  isInvited,
  onNext,
}: {
  isInvited: boolean
  onNext: () => void
}) {
  return (
    <>
      <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center text-violet-600 mb-6 mx-auto">
        <Target className="w-8 h-8" />
      </div>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
        Let's look at some<br />examples together
      </h1>
      <p className="text-xl text-gray-600 mb-4 max-w-md mx-auto">
        On the next screen, you'll see a suspicious email. Tap on anything that looks like a red flag.
      </p>
      {isInvited && (
        <p className="text-gray-500 mb-4 text-sm max-w-md mx-auto">
          Remember: no one else can see your answers. This is just for you.
        </p>
      )}
      <p className="text-sm text-gray-400 mb-8">
        Don't worry - this is just practice.
      </p>
      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-[#1d1d1f] text-white font-bold px-12 py-5 rounded-full text-lg hover:bg-black transition-all cursor-pointer shadow-xl hover:scale-[1.02] active:scale-[0.98]"
      >
        Start Practice <ChevronRight className="w-5 h-5 ml-2" />
      </button>
    </>
  )
}

function ExerciseStep({
  name,
  selectedHighlights,
  toggleHighlight,
  onNext,
}: {
  name: string
  selectedHighlights: string[]
  toggleHighlight: (id: string) => void
  onNext: () => void
}) {
  return (
    <>
      <div className="flex flex-col items-center mb-8">
        <p className="text-[13px] font-bold text-violet-600 uppercase tracking-[0.2em] mb-3">
          Interactive Practice
        </p>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Spot the Red Flags
        </h1>
        <p className="text-gray-600 text-lg max-w-md text-center">
          This email looks urgent. Tap on any part that seems suspicious.
        </p>
      </div>

      <div className="bg-white rounded-3xl overflow-hidden text-left max-w-xl mx-auto border border-gray-200 shadow-xl">
        {/* Email Header */}
        <div className="bg-gray-50 px-6 py-6 border-b border-gray-100">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 font-bold text-lg flex-shrink-0">
              B
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`cursor-pointer font-bold text-gray-900 transition-all px-1 rounded ${
                    selectedHighlights.includes('from')
                      ? 'bg-red-500 text-white'
                      : 'hover:bg-red-50'
                  }`}
                  onClick={() => toggleHighlight('from')}
                >
                  Barclays Security &lt;alert@barclay5-secure.com&gt;
                </span>
                <span className="text-[12px] text-gray-500">10:42 AM</span>
              </div>
              <div className="text-[13px] text-gray-500 flex items-center gap-1">
                <span>To:</span>
                <span className="text-gray-900">
                  {(name || 'margaret').toLowerCase().replace(' ', '.')}@email.com
                </span>
              </div>
              <div className="mt-3">
                <span
                  className={`cursor-pointer text-lg font-bold text-gray-900 leading-tight transition-all px-1 rounded inline-block ${
                    selectedHighlights.includes('subject')
                      ? 'bg-red-500 text-white'
                      : 'hover:bg-red-50'
                  }`}
                  onClick={() => toggleHighlight('subject')}
                >
                  Urgent: Unusual Activity Detected on Your Account
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Email Body */}
        <div className="p-8 space-y-6 text-[16px] leading-relaxed text-gray-900">
          <p>Dear {name || 'Margaret'},</p>
          <p>
            We've detected{' '}
            <span
              className={`cursor-pointer transition-all px-1 rounded font-medium underline decoration-red-200 decoration-2 underline-offset-4 ${
                selectedHighlights.includes('activity')
                  ? 'bg-red-500 text-white no-underline'
                  : 'hover:bg-red-50'
              }`}
              onClick={() => toggleHighlight('activity')}
            >
              unusual activity
            </span>{' '}
            on your account. To prevent unauthorized access, you must{' '}
            <span
              className={`cursor-pointer transition-all px-1 rounded font-medium underline decoration-red-200 decoration-2 underline-offset-4 ${
                selectedHighlights.includes('deadline')
                  ? 'bg-red-500 text-white no-underline'
                  : 'hover:bg-red-50'
              }`}
              onClick={() => toggleHighlight('deadline')}
            >
              verify your identity within 24 hours
            </span>
            .
          </p>
          <p>
            Click the button below to confirm your details and secure your account.
          </p>

          <div className="py-4">
            <button
              className={`w-full py-4 rounded-xl font-bold transition-all cursor-pointer shadow-lg ${
                selectedHighlights.includes('cta')
                  ? 'bg-red-500 text-white'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              onClick={() => toggleHighlight('cta')}
            >
              Verify Now
            </button>
          </div>

          <div className="pt-4 border-t border-gray-100">
            <p className="text-gray-500">
              Best regards,
              <br />
              <span className="font-semibold text-gray-900">
                Barclays Customer Protection Team
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-col items-center gap-6">
        <div
          className={`px-4 py-2 rounded-full border transition-all ${
            selectedHighlights.length > 0
              ? 'bg-red-50 border-red-100'
              : 'bg-gray-50 border-gray-100'
          }`}
        >
          <p
            className={`text-sm font-semibold ${
              selectedHighlights.length > 0 ? 'text-red-600' : 'text-gray-500'
            }`}
          >
            {selectedHighlights.length === 0
              ? 'Tap on suspicious elements in the email'
              : `${selectedHighlights.length} red flag${
                  selectedHighlights.length > 1 ? 's' : ''
                } spotted`}
          </p>
        </div>
        <button
          onClick={onNext}
          disabled={selectedHighlights.length === 0}
          className="inline-flex items-center gap-2 bg-[#1d1d1f] text-white font-bold px-12 py-5 rounded-full text-lg hover:bg-black transition-all cursor-pointer shadow-xl hover:scale-[1.02] active:scale-[0.98] disabled:opacity-30 disabled:grayscale disabled:scale-100 disabled:cursor-not-allowed"
        >
          Check My Answers <ChevronRight className="w-5 h-5 ml-2" />
        </button>
      </div>
    </>
  )
}

function ExerciseFeedbackStep({
  selectedHighlights,
  onNext,
}: {
  selectedHighlights: string[]
  onNext: () => void
}) {
  const allFlags = [
    {
      id: 'from',
      title: 'Suspicious sender',
      desc: '"barclay5-secure.com" is not barclays.co.uk',
    },
    {
      id: 'subject',
      title: 'Urgency in subject',
      desc: '"Urgent" is designed to create panic',
    },
    {
      id: 'deadline',
      title: 'Artificial deadline',
      desc: '"24 hours" pressures you into acting fast',
    },
    {
      id: 'activity',
      title: 'Vague threat',
      desc: 'Real banks give specific details about activity',
    },
    {
      id: 'cta',
      title: 'External link',
      desc: 'Always log in via the official app or website',
    },
  ]

  const foundCount = selectedHighlights.length

  return (
    <>
      <div
        className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto ${
          foundCount >= 3
            ? 'bg-emerald-100 text-emerald-600'
            : 'bg-amber-100 text-amber-600'
        }`}
      >
        {foundCount >= 3 ? (
          <CheckCircle2 className="w-8 h-8" />
        ) : (
          <Target className="w-8 h-8" />
        )}
      </div>
      <h1 className="text-3xl font-bold text-gray-900 mb-2">
        {foundCount >= 3 ? 'Great instincts!' : 'Good start!'}
      </h1>
      <p className="text-lg text-gray-600 mb-8">
        You spotted {foundCount} of {allFlags.length} red flags. Here's what to look for:
      </p>
      <div
        className="bg-white rounded-2xl border border-gray-200 max-w-lg mx-auto mb-10 overflow-hidden shadow-sm"
        style={{
          borderLeft: `6px solid ${foundCount >= 3 ? '#10b981' : '#f59e0b'}`,
        }}
      >
        {allFlags.map((item) => (
          <div
            key={item.id}
            className="flex items-start gap-4 p-5 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
          >
            <div
              className={`w-6 h-6 mt-1 flex-shrink-0 rounded-full flex items-center justify-center ${
                selectedHighlights.includes(item.id)
                  ? 'bg-emerald-100 text-emerald-600'
                  : 'bg-amber-100 text-amber-600'
              }`}
            >
              {selectedHighlights.includes(item.id) ? (
                <CheckCircle2 size={14} />
              ) : (
                <AlertTriangle size={14} />
              )}
            </div>
            <div className="text-left">
              <p className="font-bold text-[15px] text-gray-900">{item.title}</p>
              <p className="text-sm text-gray-500 leading-relaxed">
                {item.desc}
              </p>
            </div>
          </div>
        ))}
      </div>
      <p className="text-base text-emerald-600 font-bold mb-8">
        You're already getting better at this.
      </p>
      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-[#1d1d1f] text-white font-bold px-12 py-5 rounded-full text-lg hover:bg-black transition-all cursor-pointer shadow-xl hover:scale-[1.02] active:scale-[0.98]"
      >
        Continue Journey <ChevronRight className="w-5 h-5 ml-2" />
      </button>
    </>
  )
}

function PillarsIntroStep({ onNext }: { onNext: () => void }) {
  const pillars = [
    {
      icon: <ShieldCheck className="w-6 h-6" />,
      name: 'PROTECT',
      desc: 'Secure your accounts and devices',
    },
    {
      icon: <Search className="w-6 h-6" />,
      name: 'DETECT',
      desc: 'Spot scams before they work',
    },
    {
      icon: <LifeBuoy className="w-6 h-6" />,
      name: 'RESPOND',
      desc: 'Know what to do when targeted',
    },
    {
      icon: <Users className="w-6 h-6" />,
      name: 'CONNECT',
      desc: 'Protect your family together',
    },
  ]

  return (
    <>
      <p className="text-xs font-bold text-violet-600 uppercase tracking-widest mb-6">
        Your Training Path
      </p>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
        You've learned<br />the foundation
      </h1>
      <p className="text-xl text-gray-600 mb-8 max-w-lg mx-auto">
        Now let's build your complete defense. GranGuard training covers four areas of protection:
      </p>
      <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto mb-10">
        {pillars.map((pillar) => (
          <div
            key={pillar.name}
            className="p-5 bg-gray-50 rounded-2xl text-center border border-gray-100"
          >
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-violet-600 mx-auto mb-3 shadow-sm">
              {pillar.icon}
            </div>
            <div className="font-bold text-sm text-gray-900 mb-1">
              {pillar.name}
            </div>
            <div className="text-xs text-gray-500">{pillar.desc}</div>
          </div>
        ))}
      </div>
      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-[#1d1d1f] text-white font-bold px-12 py-5 rounded-full hover:bg-black transition-all cursor-pointer shadow-xl hover:scale-[1.02] active:scale-[0.98]"
      >
        Where Should I Start? <ChevronRight className="w-5 h-5 ml-2" />
      </button>
    </>
  )
}

function PillarsChoiceStep({
  selectedPillar: _selectedPillar,
  setSelectedPillar,
  onNext,
}: {
  selectedPillar: 'protect' | 'detect' | null
  setSelectedPillar: (pillar: 'protect' | 'detect') => void
  onNext: () => void
}) {
  return (
    <>
      <p className="text-xs font-bold text-violet-600 uppercase tracking-widest mb-6">
        Choose Your Path
      </p>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
        We recommend<br />starting with PROTECT
      </h1>
      <p className="text-lg text-gray-600 mb-8 max-w-lg mx-auto">
        Strong passwords and secure accounts are your first line of defense. Everything else builds on this foundation.
      </p>
      <div className="w-full max-w-lg mx-auto space-y-4">
        <button
          onClick={() => {
            setSelectedPillar('protect')
            onNext()
          }}
          className="w-full p-6 rounded-3xl border-2 border-violet-600 bg-violet-50 text-left flex justify-between items-start cursor-pointer transition-all hover:-translate-y-1 hover:shadow-lg"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-violet-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <div className="font-bold text-violet-900 mb-1">
                Start with PROTECT
              </div>
              <div className="text-sm text-violet-700/70">
                Build your security foundation first
              </div>
            </div>
          </div>
          <div className="bg-violet-600 text-white text-xs font-bold px-3 py-1 rounded-full flex-shrink-0">
            RECOMMENDED
          </div>
        </button>
        <button
          onClick={() => {
            setSelectedPillar('detect')
            onNext()
          }}
          className="w-full p-6 rounded-3xl border-2 border-gray-200 hover:border-violet-300 text-left flex items-start gap-4 cursor-pointer transition-all hover:-translate-y-1"
        >
          <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center text-gray-500 flex-shrink-0">
            <Search className="w-6 h-6" />
          </div>
          <div>
            <div className="font-bold text-gray-900 mb-1">Jump to DETECT</div>
            <div className="text-sm text-gray-500">
              Learn to spot scams right away
            </div>
          </div>
        </button>
        <button
          onClick={onNext}
          className="text-sm font-medium text-gray-500 hover:text-violet-600 transition-colors mt-4 cursor-pointer"
        >
          Browse all training →
        </button>
      </div>
    </>
  )
}

// ==============================================================================
// SHARED STEP COMPONENTS
// ==============================================================================

function ShieldCheckStep({
  isInvited,
  progress,
  setProgress,
  isComplete,
  setIsComplete,
  onNext,
}: {
  isInvited: boolean
  progress: number
  setProgress: React.Dispatch<React.SetStateAction<number>>
  isComplete: boolean
  setIsComplete: (complete: boolean) => void
  onNext: () => void
}) {
  const [currentCheck, setCurrentCheck] = useState(0)

  const checks = [
    { label: 'Checking email address...', done: 'Email address checked' },
    { label: 'Checking phone number...', done: 'Phone number checked' },
    { label: 'Searching known breaches...', done: 'Breach databases searched' },
    { label: 'Analyzing exposure level...', done: 'Analysis complete' },
  ]

  useEffect(() => {
    if (isComplete) return

    const progressInterval = setInterval(() => {
      setProgress((prev: number) => {
        if (prev >= 100) {
          clearInterval(progressInterval)
          return 100
        }
        return prev + 2
      })
    }, 100)

    const checkInterval = setInterval(() => {
      setCurrentCheck((prev) => {
        if (prev >= checks.length - 1) {
          clearInterval(checkInterval)
          return prev
        }
        return prev + 1
      })
    }, 1500)

    const completeTimeout = setTimeout(() => {
      setIsComplete(true)
    }, 6000)

    return () => {
      clearInterval(progressInterval)
      clearInterval(checkInterval)
      clearTimeout(completeTimeout)
    }
  }, [isComplete, setProgress, setIsComplete])

  if (!isComplete) {
    return (
      <>
        <div className="w-24 h-24 bg-gradient-to-br from-amber-100 to-orange-100 rounded-[28px] flex items-center justify-center mb-8 shadow-lg mx-auto border border-amber-200/50 relative">
          <Shield className="w-12 h-12 text-amber-500" />
          <motion.div
            className="absolute inset-0 rounded-[28px] border-2 border-amber-400"
            animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Checking Your Shield
        </h1>
        {isInvited && (
          <p className="text-sm text-gray-500 mb-2">
            Only you can see these results
          </p>
        )}
        <p className="text-gray-500 mb-8">This usually takes about 10 seconds</p>

        <div className="w-full max-w-sm mx-auto mb-8">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-6">
            <motion.div
              className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>

          <div className="space-y-3 text-left">
            {checks.map((check, i) => (
              <div key={i} className="flex items-center gap-3">
                {i < currentCheck ? (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center"
                  >
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  </motion.div>
                ) : i === currentCheck ? (
                  <motion.div
                    className="w-5 h-5 border-2 border-amber-400 rounded-full border-t-transparent"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  />
                ) : (
                  <div className="w-5 h-5 border-2 border-gray-200 rounded-full" />
                )}
                <span
                  className={`text-sm ${
                    i <= currentCheck ? 'text-gray-900' : 'text-gray-500'
                  }`}
                >
                  {i < currentCheck ? check.done : check.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-gray-500">
          Checking against hundreds of known data breaches
        </p>
      </>
    )
  }

  // Results view
  return (
    <>
      <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-[20px] flex items-center justify-center mb-6 shadow-lg mx-auto border border-amber-200/50">
        <Shield className="w-8 h-8 text-amber-500" />
      </div>
      <p className="text-xs font-bold text-amber-600 uppercase tracking-widest mb-2">
        Shield Check Results
      </p>
      <h2 className="text-2xl font-bold text-gray-900 mb-4">
        Found in 3 breaches
      </h2>

      <div className="bg-blue-50 rounded-xl border border-blue-100 p-4 max-w-sm mx-auto mb-4">
        <p className="text-blue-800 text-sm">
          <strong>You're not alone</strong> - 87% of adults have been in at least one data breach.
        </p>
      </div>

      <p className="text-gray-600 mb-6 max-w-md mx-auto text-sm">
        The training ahead will show you exactly how scammers might use this - and how to stop them.
      </p>

      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-[#1d1d1f] text-white font-bold px-12 py-5 rounded-full hover:bg-black transition-all cursor-pointer shadow-xl hover:scale-[1.02] active:scale-[0.98]"
      >
        Continue <ArrowRight className="w-5 h-5 ml-2" />
      </button>
    </>
  )
}

function WelcomeKitAddressStep({
  addressInput,
  setAddressInput,
  selectedAddress,
  setSelectedAddress,
  isSubmitting,
  isGuardian,
  onSubmit,
  onSkip,
}: {
  addressInput: string
  setAddressInput: (value: string) => void
  selectedAddress: {
    address: string
    city: string
    state: string
    country: string
    postcode: string
    unitNumber?: string
  } | null
  setSelectedAddress: (address: {
    address: string
    city: string
    state: string
    country: string
    postcode: string
    unitNumber?: string
  } | null) => void
  isSubmitting: boolean
  isGuardian: boolean
  onSubmit: () => void
  onSkip: () => void
}) {
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null)
  const [isValidating, setIsValidating] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [countryCode, setCountryCode] = useState<string>('GB')

  const handleAddressSelected = async (addr: {
    address: string
    city: string
    state: string
    country: string
    postcode: string
    countryCode?: string
  }) => {
    setSubmitError(null)
    setSelectedAddress({
      address: addr.address,
      city: addr.city,
      state: addr.state,
      country: addr.country,
      postcode: addr.postcode,
    })
    if (addr.countryCode) {
      setCountryCode(addr.countryCode)
    }

    // Validate the address
    setIsValidating(true)
    try {
      const result = await validateAddressWithGoogle({
        addressLine1: addr.address,
        locality: addr.city,
        administrativeArea: addr.state,
        postalCode: addr.postcode,
        countryCode: addr.countryCode || 'GB',
      })
      setValidationResult(result)
    } catch {
      setValidationResult({ status: 'UNCONFIRMED' })
    } finally {
      setIsValidating(false)
    }
  }

  const handleSubmit = async () => {
    if (!selectedAddress) return

    setSubmitError(null)
    setIsValidating(true)

    try {
      // Build full address with unit number
      const fullAddressLine = selectedAddress.unitNumber
        ? `${selectedAddress.unitNumber}, ${selectedAddress.address}`
        : selectedAddress.address

      // Re-validate with unit number included
      const result = await validateAddressWithGoogle({
        addressLine1: fullAddressLine,
        locality: selectedAddress.city,
        administrativeArea: selectedAddress.state,
        postalCode: selectedAddress.postcode,
        countryCode: countryCode,
      })

      if (result.status === 'INVALID') {
        setSubmitError('This address could not be verified. Please check and try again.')
        setIsValidating(false)
        return
      }

      // Address is valid or unconfirmed (good enough) - proceed
      setIsValidating(false)
      onSubmit()
    } catch {
      setSubmitError('Could not verify address. Please try again.')
      setIsValidating(false)
    }
  }

  const needsUnitNumber = validationResult?.status === 'UNCONFIRMED' || validationResult?.status === 'INVALID'

  return (
    <>
      <motion.img
        src="/welcomekit.webp"
        alt="GranGuard Welcome Kit"
        className="w-56 md:w-64 mx-auto mb-6"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      />
      <motion.h1
        className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.12, duration: 0.4, ease: 'easeOut' }}
      >
        Kit Delivery
      </motion.h1>

      <motion.div
        className="max-w-md mx-auto mb-8"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.24, duration: 0.4, ease: 'easeOut' }}
      >
        <AddressAutocomplete
          value={addressInput}
          onInputChange={setAddressInput}
          onAddressSelected={handleAddressSelected}
          autoFocus
          placeholder={isGuardian
            ? "Enter an address to receive your kit..."
            : "Enter your address and we'll ship your kit..."}
        />

        {selectedAddress && (
          <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100 text-left relative">
            <button
              onClick={() => {
                setSelectedAddress(null)
                setAddressInput('')
                setValidationResult(null)
                setSubmitError(null)
              }}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              aria-label="Clear address"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3 pr-6">
              <MapPin className="w-5 h-5 text-violet-600 mt-0.5 flex-shrink-0" />
              <div className="text-gray-700 flex-1">
                {selectedAddress.unitNumber && (
                  <p className="font-medium">{selectedAddress.unitNumber}</p>
                )}
                <p className="font-medium">{selectedAddress.address}</p>
                <p className="text-sm text-gray-500">
                  {[selectedAddress.city, selectedAddress.state, selectedAddress.postcode].filter(Boolean).join(', ')}
                </p>
                {selectedAddress.country && (
                  <p className="text-sm text-gray-500">{selectedAddress.country}</p>
                )}
              </div>
            </div>

            {isValidating && (
              <p className="text-sm text-gray-500 mt-3">Checking address...</p>
            )}

            {!isValidating && needsUnitNumber && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Flat / Unit number (if applicable)
                </label>
                <input
                  type="text"
                  value={selectedAddress.unitNumber || ''}
                  onChange={(e) => setSelectedAddress({ ...selectedAddress, unitNumber: e.target.value })}
                  placeholder="e.g. Flat 4, Unit 2B"
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
            )}
          </div>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.36, duration: 0.4, ease: 'easeOut' }}
      >
        {submitError && (
          <div className="max-w-md mx-auto mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {submitError}
          </div>
        )}

        <div className="flex flex-col items-center gap-4">
        <div className="relative group">
          <button
            onClick={handleSubmit}
            disabled={!selectedAddress || isSubmitting || isValidating}
            className={`inline-flex items-center gap-2 font-semibold px-10 py-4 rounded-full text-lg transition-all ${!selectedAddress || isSubmitting || isValidating ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-violet-600 text-white hover:bg-violet-700 cursor-pointer shadow-lg shadow-violet-200'}`}
          >
            {isValidating ? 'Verifying...' : isSubmitting ? 'Saving...' : 'Send my kit'}
            <ChevronRight className="w-5 h-5" />
          </button>
          {!selectedAddress && !isSubmitting && !isValidating && (
            <div className="absolute -top-12 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-lg whitespace-nowrap pointer-events-none shadow-lg">
              Enter an address above to continue
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-[6px] border-transparent border-t-gray-900" />
            </div>
          )}
        </div>
        <button
          onClick={onSkip}
          disabled={isSubmitting || isValidating}
          className="text-gray-500 text-base font-medium hover:text-violet-600 transition-colors cursor-pointer disabled:opacity-50"
        >
          I'll set this up later
        </button>
      </div>
      </motion.div>
    </>
  )
}

function CompleteStep({
  path,
  name,
  isSubmitting,
  onComplete,
  hasWelcomeKit,
  familyMembers,
  weeklyTipsEnabled,
  setWeeklyTipsEnabled,
}: {
  path: OnboardingPath
  name: string
  invitedCount: number
  isSubmitting: boolean
  onComplete: () => void
  hasWelcomeKit: boolean
  familyMembers?: FamilyMember[]
  weeklyTipsEnabled?: boolean
  setWeeklyTipsEnabled?: React.Dispatch<React.SetStateAction<boolean>>
}) {
  // For guardians, show enhanced completion with family status and weekly tips
  const isGuardian = path === 'GUARDIAN'
  const addedMembers = familyMembers?.filter((m) => m.email && m.name) || []

  return (
    <>
      <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-[24px] flex items-center justify-center text-white mb-6 shadow-xl mx-auto">
        <CheckCircle2 className="w-10 h-10" />
      </div>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
        You're all set{name ? `, ${name}` : ''}!
      </h1>

      {/* GUARDIAN: Family Status Card */}
      {isGuardian && addedMembers.length > 0 && (
        <div className="max-w-md mx-auto mb-6">
          <div className="bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-500">
                Family Status
              </p>
            </div>
            <div className="divide-y divide-gray-100">
              {addedMembers.map((member) => (
                <div key={member.id} className="flex items-center justify-between px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-violet-600">
                        {member.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="font-medium text-gray-900">{member.name}</span>
                  </div>
                  <span className="text-sm text-gray-500">Invitation sent</span>
                </div>
              ))}
              <div className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-emerald-600">
                      {name ? name.charAt(0).toUpperCase() : 'Y'}
                    </span>
                  </div>
                  <span className="font-medium text-gray-900">You</span>
                </div>
                <span className="text-sm text-emerald-600 font-medium">Ready to explore</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* GUARDIAN: Weekly Tips Opt-in */}
      {isGuardian && setWeeklyTipsEnabled && (
        <div className="max-w-md mx-auto mb-6">
          <div className="bg-violet-50 rounded-2xl border border-violet-200 p-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-violet-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-900">Weekly Tips</p>
                <p className="text-sm text-gray-600">
                  One tip per week — takes 30 seconds to read
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setWeeklyTipsEnabled(true)}
                className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                  weeklyTipsEnabled
                    ? 'bg-violet-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-700 hover:border-violet-300'
                }`}
              >
                Yes please
              </button>
              <button
                onClick={() => setWeeklyTipsEnabled(false)}
                className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                  !weeklyTipsEnabled
                    ? 'bg-violet-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-700 hover:border-violet-300'
                }`}
              >
                Just alerts
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Non-guardian paths keep original messaging */}
      {path === 'GUARDIAN' && addedMembers.length === 0 && (
        <div className="max-w-md mx-auto mb-8">
          <p className="text-xl text-gray-600">
            Your dashboard is ready. You can invite family members anytime from your account settings.
          </p>
        </div>
      )}

      {path === 'SELF_PROTECTOR' && (
        <p className="text-xl text-gray-600 mb-8 max-w-md mx-auto">
          Your personalized training path is ready. Let's build your scam defense skills.
        </p>
      )}

      {path === 'INVITED' && (
        <p className="text-xl text-gray-600 mb-8 max-w-md mx-auto">
          Your training is ready. Remember - everything you learn here stays between you and GranGuard.
        </p>
      )}

      {/* Welcome Kit Status */}
      {hasWelcomeKit && path !== 'INVITED' && (
        <div className="bg-amber-50 rounded-2xl p-5 max-w-md mx-auto mb-6 border border-amber-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <Package className="w-5 h-5 text-amber-600" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-amber-900">Welcome Kit on the way</p>
              <p className="text-sm text-amber-700">Track delivery in your dashboard</p>
            </div>
          </div>
        </div>
      )}

      {/* Next steps - only for non-guardians (guardians already have family status + weekly tips) */}
      {!isGuardian && (
        <div className="bg-gray-50 rounded-2xl p-6 max-w-md mx-auto mb-8 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4">Next steps:</h3>
          <div className="space-y-3 text-left">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-violet-600" />
              <span className="text-gray-700">Get familiar with your dashboard</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-violet-600" />
              <span className="text-gray-700">Explore the Scam Defense Starter Guide</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-violet-600" />
              <span className="text-gray-700">Check out the data breach monitor</span>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={onComplete}
        disabled={isSubmitting}
        className="inline-flex items-center gap-2 bg-[#1d1d1f] text-white font-bold px-12 py-5 rounded-full text-xl shadow-2xl hover:bg-black transition-all cursor-pointer disabled:opacity-50 group hover:scale-[1.02] active:scale-[0.98]"
      >
        {isSubmitting ? 'Loading...' : 'Go to Dashboard'}
        <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform ml-2" />
      </button>
    </>
  )
}
